import type { SupabaseClient } from '@supabase/supabase-js';
import { createGrid, type ColDef, type GridApi, type GridOptions, type ICellRendererParams, type IRowNode, type ValueSetterParams } from 'ag-grid-community';
import { showToast, showProgressToast } from '../../lib/toast.js';
import { awaitJob } from '../../lib/await-job.js';
import { summarizeBulkOutcome, jobStepLabel, type BulkJobOutcome } from '../../lib/bulk-failure-text.js';
import { wartehinweis } from '../../lib/job-wait-text.js';
import { varioArticleUrl, varioListingUrl } from '../../lib/vario-links.js';
import { gambioProductId, gambioProductFrontendUrl, gambioProductAdminUrl } from '../../lib/gambio-links.js';
import { productFilterConditions } from '../../lib/products-filter.js';
import { seedKandidaten, inaktivHinweis } from '../../lib/products-seed-eligibility.js';
import { openModal, openErrorModal } from '@vario-connect/frontend-design';

interface ProductsCtx {
  supabase: SupabaseClient;
  session: { token: string; tenantId: string };
  supabaseUrl: string;
}

/**
 * Eine Zeile aus `gambio.products_overview_ui` (Migrationen 0344/0346) — die Sicht, die den
 * frueheren clientseitigen Join ueber fuenf Tabellen ersetzt. Die `payload`-Skalare kommen als
 * Spalten, `price`/`quantity` als `numeric` und damit ueber PostgREST als String.
 */
interface OverviewRow {
  id_item: string;
  storefront: string;
  sku: string | null;
  ean: string | null;
  title: string | null;
  price: string | number | null;
  quantity: string | number | null;
  is_active: boolean | null;
  effective_is_active: boolean | null;
  effective_tax_class_id: number | null;
  is_variant: boolean;
  is_variant_main: boolean;
  variant_count: number | null;
  parent_model: string | null;
  vario_article_id: string | null;
  vario_article_number: string | null;
  /** Gesetzt, wenn der Reconcile-Lauf den VARIO-Artikel nicht mehr findet (0360/0361). */
  vario_article_missing_since: string | null;
  vario_stock: number | null;
  channel_price_cents: number | null;
  min_bestand: number | null;
  max_bestand: number | null;
  vario_product_type: string | null;
  suggested_vario_article_id: string | null;
  suggested_vario_article_number: string | null;
  suggested_match_source: 'number' | 'ean' | null;
  listing_id: string | null;
  vario_listing_id: string | null;
  vario_main_article_id: string | null;
  parent_vario_article_id: string | null;
  parent_listing_id: string | null;
  /**
   * Ist DIESE Zeile zugeordnet? Deckungsgleich mit der Anzeige der Spalte „Vario SKU": eine
   * eigene Nummer, oder beim Variantenhauptartikel der verlinkte Vario-Vaterartikel. Der
   * Hauptartikel ist ein Container ohne eigene Nummer, `vario_article_number` ist bei ihm IMMER
   * leer (Migration 0364).
   */
  is_mapped: boolean;
  /**
   * Zustand der ganzen Variantengruppe (Flachprodukt = Gruppe aus sich selbst). Nur der
   * Zuordnungs-Filter liest sie, deshalb stehen sie nicht in `SELECT_COLS`.
   */
  group_has_mapped?: boolean;
  group_has_unmapped?: boolean;
  group_has_in_vario?: boolean;
}

interface GridRow {
  idItem: string;
  storefront: string;
  sku: string | null;
  ean: string | null;
  title: string | null;
  price: number | null;
  quantity: number | null;
  isActive: boolean | null;
  taxClassId: number | null;   // eigene (Eltern) bzw. via parentId geerbte Gambio-Steuerklasse
  varioArticleNumber: string | null; // aus mapping; null = unmapped
  varioArticleMissingSince: string | null; // gesetzt = VARIO kennt den Artikel nicht mehr
  varioArticleId: string | null; // aus mapping; für Deeplink in die Vario-Artikel-Detailseite
  varioMainArticleId: string | null; // nur Variantenprodukte: der VARIO-Variantenhauptartikel (aus variant_groups) für den Deeplink
  varioListingId: string | null; // aus gambio.listings; für Deeplink in den Vario-Verkaufskanal (Kanal-Listing)
  gambioListingId: string | null; // gambio.listings.id (interne UUID); für gambio-variant-rows list/save
  varioStock: number | null;
  channelPriceCents: number | null; // zuletzt gesetzter Ziel-Netto-Preis (Cent); ≠ Gambio-Netto → Preis-Push steht aus
  minBestand: number | null;
  maxBestand: number | null;
  varioProductType: string | null;
  isVariant: boolean;          // marketplace_unit_id = <pId>-v<vId> → Gambio-Variante
  isVariantMain: boolean;      // payload.isVariantMain → Gambio-Variantenhauptartikel (eigene Listenzeile)
  variantCount: number | null; // Anzahl Varianten-Kombis (nur am Hauptartikel gesetzt)
  parentModel: string | null;  // Gambio parentModel (Variantenfamilie); nur bei Varianten gesetzt
  suggestedVarioId: string | null;
  suggestedVarioNumber: string | null;
  matchSource: 'number' | 'ean' | null;
  // Elterndaten der Variantenzeile, aus `gambio.products_overview_ui`. Sie ersetzen die frueheren
  // Suchen ueber ALLE Zeilen (`forEachNode`), die beim seitenweisen Laden still falsch waeren,
  // weil die Elternzeile gar nicht geladen sein muss.
  parentVarioArticleId: string | null;
  parentListingId: string | null;
  // Wirksamer Aktiv-Status: eigener Wert, bei Varianten der des Elternprodukts. Er speist den
  // Frontend-Link UND seit dem 21.09.2026 den Status-Filter. `isActive` bleibt daneben der EIGENE
  // Wert, weil die Spalte „Gambio-Status" ihn anzeigt: eine Variante hat keinen eigenen Schalter
  // und muss weiterhin „—" zeigen, statt den Wert des Vaters als eigenen auszugeben.
  effectiveIsActive: boolean | null;
  // Zugeordnet im Sinne der Sicht: eigene Vario-Nummer ODER Variantenhauptartikel mit verlinktem
  // Vario-Vaterartikel. Nicht durch `!varioArticleNumber` ersetzbar - der Hauptartikel hat NIE
  // eine eigene Nummer und wuerde sonst dauerhaft als offener Posten gelten.
  isMapped: boolean;
}

// --- Preis-Editor (Kundengruppen-/Staffelpreise) ---
// Antwort-Vertrag der Edge Function `gambio-product-prices` (action:'get'). `groups` = ein
// Eintrag je GEMAPPTER Kundengruppe, `tiers` aufsteigend nach fromQuantity. `gambioGroups`
// sind die aktuell in Gambio hinterlegten NETTO-Referenzwerte (für die „übernehmen"-Hilfe).
interface PriceGetTier { fromQuantity: number; net: number | null; gross: number | null; priceId: string; priceBase: 'NET_PRICE' | 'GROSS_PRICE'; inherited?: boolean; }
interface PriceGetGroup { gambioStatusId: number; varioPriceGroupId: string | null; varioCustomerGroupId: string | null; label?: string | null; tiers: PriceGetTier[]; }
interface PriceGetGambioGroup { customerGroupId: number; groupPrice: number; graduatedPrices: Array<{ graduatedPrice: number; threshold: number }>; }
interface GetResp {
  channelId: string | null;
  varioArticleId?: string;
  mappingCount?: number;
  note?: string;
  taxFactor?: number;
  groups: PriceGetGroup[];
  gambioGroups?: PriceGetGambioGroup[];
}
interface PriceSaveResp { ok: boolean; created: number; updated: number; deleted: number; errors: string[]; }

function escapeHtml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}
function fmtPrice(v: number | null): string {
  return v === null || Number.isNaN(v) ? '—' : `${v.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
}
/** Netto→Brutto mit dem Steuerklassen-Satz des Shops. Kein Satz bekannt → Netto unverändert. */
function grossForRow(net: number | null, taxClassId: number | null, rates: Record<string, number>): number | null {
  if (net === null || Number.isNaN(net)) return null;
  const rate = taxClassId != null ? rates[String(taxClassId)] : undefined;
  return rate != null ? net * (1 + rate / 100) : net;
}
// Bestandslose Vario-Artikelarten (kein echter Lagerbestand). Spiegel des Workers
// (apps/worker/src/lib/gambio-export-resolve.ts STOCKLESS_PRODUCT_TYPES).
const STOCKLESS_PRODUCT_TYPES = new Set(['WITHOUT_STOCK', 'SERVICE_ARTICLE']);
// Automatischer Bestands-Export läuft nur in diese Richtung (Spiegel von exportEnabled im Worker,
// forced=false); andere Richtung → der Cron fasst den Gambio-Bestand nie an.
const AUTO_EXPORT_SYNC_DIRECTION = 'vario_to_gambio';
const DEFAULT_SYNC_DIRECTION = 'vario_to_gambio';

/**
 * Preis-Push steht aus: der zuletzt gesetzte Ziel-Netto-Preis (`channelPriceCents`) weicht vom
 * aktuellen Gambio-Netto (`price`, aus gambio.items.payload.price) ab. Solange true → Zelle orange
 * + das Grid zeigt den ZIEL-Preis. Kein Ziel gesetzt (null) → nie markieren (Otto-Muster).
 * 1-Cent-Toleranz: bei Brutto-Eingabe wird das Ziel-Netto über den Steuerfaktor zurueckgerechnet,
 * das kann gegenueber Gambios eigener Netto-Ableitung um einen Cent abweichen. Ohne Toleranz
 * bliebe die Zelle dann faelschlich dauerhaft orange, obwohl der Push laengst durch ist.
 */
export function pricePushPending(row: { price: number | null; channelPriceCents: number | null }): boolean {
  if (row.channelPriceCents == null) return false;
  const currentCents = row.price != null ? Math.round(row.price * 100) : null;
  return currentCents == null || Math.abs(currentCents - row.channelPriceCents) > 1;
}

/**
 * Bestand-Push steht aus (Zelle orange): der nächste automatische Abgleich würde den Gambio-Bestand
 * TATSÄCHLICH ändern. 1:1-Spiegel des Workers `gambio-push-stock.ts` (inkl. `exportEnabled` sowie
 * `stockTarget`/`isStockless` aus `gambio-export-resolve.ts`). Referenz-/Regressionstest liegt in
 * `apps/worker/src/lib/gambio-stock-push-pending.test.ts`:
 *   - Richtungs-Gate: der automatische Abgleich (forced=false) exportiert NUR bei
 *     sync_direction='vario_to_gambio'. Andere Richtung → nie markieren (sonst irreführend);
 *   - nur gemappte, bestandsgeführte Zeilen (Hauptartikel hat keinen eigenen Bestand → nie);
 *   - bestandslos (WITHOUT_STOCK/SERVICE_ARTICLE) → Ziel = `maxBestand ?? virtueller Bestand`;
 *   - bestandsführend & Vario-Bestand fehlt (null) → Worker skippt → kein Push;
 *   - bestandsführend mit Min+Max → `stockTarget`-Hysterese (Trigger: Gambio-Bestand > Max ODER
 *     Gambio-Bestand ≤ Min ODER Vario < Min ODER Gambio-Bestand unbekannt); sonst ohne Min/Max
 *     1:1 der Vario-Bestand;
 *   - Push nur, wenn das Ziel ≠ aktueller Gambio-Bestand (`quantity`) ist.
 *
 * Vergleichsbasis ist der Gambio-Bestand, NICHT der zuletzt gepushte Wert — der Worker entscheidet
 * genauso (siehe `stockTarget`).
 */
export function stockPushPending(
  row: {
    varioArticleId: string | null;
    isVariantMain: boolean;
    varioProductType: string | null;
    varioStock: number | null;
    quantity: number | null;
    minBestand: number | null;
    maxBestand: number | null;
  },
  virtualStock: number,
  syncDirection: string,
): boolean {
  // Richtungs-Gate wie exportEnabled(direction, forced=false) im Worker: kein automatischer
  // Export → der Cron ändert den Gambio-Bestand nie, also nie markieren.
  if (syncDirection !== AUTO_EXPORT_SYNC_DIRECTION) return false;
  if (!row.varioArticleId || row.isVariantMain) return false; // nur gemappte, bestandsgeführte Zeilen
  const min = row.minBestand;
  const max = row.maxBestand;
  const gambioStock = row.quantity;
  let target: number | null;
  if (STOCKLESS_PRODUCT_TYPES.has(row.varioProductType ?? '')) {
    target = row.maxBestand ?? virtualStock;
  } else if (row.varioStock === null) {
    return false; // Refresh ausstehend → Worker skippt (sonst würde er 0 pushen)
  } else {
    // stockTarget-Spiegel:
    const v = Math.trunc(row.varioStock);
    if (min !== null && max !== null) {
      const trigger = gambioStock === null || gambioStock > max || gambioStock <= min || v < min;
      target = trigger ? Math.max(0, Math.min(v, max)) : null;
    } else {
      target = Math.max(0, v);
    }
  }
  return target !== null && target !== gambioStock;
}

function productTypeLabel(t: string | null): string {
  if (t === 'WITH_STOCK') return 'mit Bestand';
  if (t === 'WITHOUT_STOCK') return 'ohne Bestand';
  if (t === 'SERVICE_ARTICLE') return 'Dienstleistung';
  return '—';
}

// Inline-SVG-Icons (Lucide-Stil, currentColor) - matchen das neue .go-products-Design.
const SVG_ART_MAIN = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="6" cy="6" r="3"/><circle cx="6" cy="18" r="3"/><path d="M20 4 8.6 15.4M14 4h6v6"/></svg>';
const SVG_ART_VARIANT = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16Z"/><path d="m3.3 7 8.7 5 8.7-5M12 22V12"/></svg>';
const SVG_PEN = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 20h9"/><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4z"/></svg>';
const SVG_EXTERNAL = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M15 3h6v6M10 14 21 3M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/></svg>';
const SVG_DOWNLOAD = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><path d="M7 10l5 5 5-5M12 15V3"/></svg>';
const SVG_UPLOAD = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><path d="M7 9l5-5 5 5M12 4v12"/></svg>';
const SVG_UNBIND = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M9.5 14.5 4 20M14 8l6-6M10 6l8 8M3 13l8 8"/></svg>';
const SVG_LINK = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M10 13a5 5 0 0 0 7 0l3-3a5 5 0 0 0-7-7l-1 1"/><path d="M14 11a5 5 0 0 0-7 0l-3 3a5 5 0 0 0 7 7l1-1"/></svg>';
const SVG_PLUS = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M12 8v8M8 12h8"/></svg>';
const SVG_SEARCH = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="11" cy="11" r="7"/><path d="m21 21-4-4"/></svg>';
const SVG_REFRESH = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 12a9 9 0 1 1-6.2-8.5"/><path d="M21 3v6h-6"/></svg>';
const SVG_INFO = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="9"/><path d="M9.5 9a2.5 2.5 0 1 1 3 2.5V14"/><path d="M12 17h.01"/></svg>';
const SVG_MINMAX = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="m12 2 9 5-9 5-9-5 9-5Z"/><path d="m3 12 9 5 9-5M3 17l9 5 9-5"/></svg>';

// Mehrwertsteuer-Faktor für die unverbindliche Netto/Brutto-Umrechnung im Preis-Editor.
// NUR Anzeige-Richtwert bzw. Umrechnung des Gambio-Netto-Werts in die Gruppen-Basis —
// wird NIE an die Save-Route geschickt (die speichert exakt die eingegebenen Basis-Preise).
const PRICE_VAT_FACTOR = 1.19;

/**
 * Modal: Kundengruppen-/Staffelpreise eines gemappten Nicht-Varianten-Produkts bearbeiten.
 * Lädt die Preise per Edge Function `gambio-product-prices` (action:'get'), lässt pro
 * gemappter Kundengruppe Staffeln (Menge ab | Preis) editieren und speichert per action:'save'.
 * Nach erfolgreichem Speichern wird der artikelscoped Gambio-Push angestoßen und `onSaved()`
 * gerufen (Grid-Reload). Kein window.confirm/alert - Validierung inline im Modal.
 */
/**
 * Ruft die Edge Function `gambio-product-prices` über das in dieser App etablierte Muster auf:
 * rohes fetch gegen `ctx.supabaseUrl` mit dem Session-Token - NICHT `supabase.functions.invoke`
 * (das zeigt hier auf die falsche/blockierte URL → "Failed to send a request"). Liefert dieselbe
 * `{ data, error }`-Form wie invoke, damit die Aufrufer unverändert bleiben.
 */
async function callProductPrices(
  ctx: ProductsCtx, body: Record<string, unknown>,
): Promise<{ data: unknown; error: { message: string; json?: unknown } | null }> {
  try {
    const res = await fetch(`${ctx.supabaseUrl}/functions/v1/gambio-product-prices`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${ctx.session.token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    const data = (await res.json().catch(() => null)) as unknown;
    if (!res.ok) {
      const errObj = (data as { error?: { message?: string } } | null)?.error;
      // json = voller Response-Body (inkl. error.detail = Vario-Originalfehler) für das Fehler-Modal.
      return { data: null, error: { message: errObj?.message ?? `HTTP ${res.status}`, json: data } };
    }
    return { data, error: null };
  } catch (e) {
    return { data: null, error: { message: (e as Error).message } };
  }
}

/** Roh-Fetch gegen gambio-variant-rows (list/save) — analog callProductPrices, eigener Slug. */
async function callVariantRows(
  ctx: ProductsCtx, body: Record<string, unknown>,
): Promise<{ data: unknown; error: { message: string; json?: unknown } | null }> {
  try {
    const res = await fetch(`${ctx.supabaseUrl}/functions/v1/gambio-variant-rows`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${ctx.session.token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    const data = (await res.json().catch(() => null)) as unknown;
    if (!res.ok) {
      const errObj = (data as { error?: { message?: string } } | null)?.error;
      return { data: null, error: { message: errObj?.message ?? `HTTP ${res.status}`, json: data } };
    }
    return { data, error: null };
  } catch (e) {
    return { data: null, error: { message: (e as Error).message } };
  }
}

/**
 * Genau EIN Preis-Modal (Vater-/Flachartikel) gleichzeitig — dieselbe Sperre wie
 * `variantPriceModalOpen` für die Varianten-Kinder. Wird im `onClose` zurückgesetzt.
 */
let priceModalOpen = false;

async function openPriceModal(ctx: ProductsCtx, itemId: string, storefront: string, onSaved: () => void): Promise<void> {
  if (priceModalOpen) return; // Klick-Sperre gegen Mehrfach-Öffnen

  const body = `
    <div class="kl-form-hint" style="margin-bottom:12px;">Staffelpreise je Kundengruppe. „Menge ab 1" ist der Grundpreis. Netto und Brutto sind beide direkt eingebbar - die jeweils andere Seite wird automatisch berechnet. <strong>Feld leer lassen = kein eigener Preis</strong> für diese Kundengruppe (Gambio nutzt dann den Standardpreis). Eine 0 ist dagegen ein echter Nullpreis.</div>
    <div id="gm-price-groups"></div>
    <div class="kl-modal-error" id="gm-price-error" hidden style="margin-top:10px;"></div>
  `;
  const footer = `
    <button type="button" class="v-button" data-modal-cancel>Abbrechen</button>
    <button type="button" class="v-button v-button--save" data-action="save"><span class="v-icon fa-regular fa-check"></span> Preise speichern</button>
  `;
  priceModalOpen = true;
  const modal = openModal({
    title: 'Preise bearbeiten', icon: 'fa-tags', width: 640, body, footer,
    // Schonfrist: der zweite Klick eines Doppelklicks trifft sonst das Backdrop und schliesst sofort.
    dismissGraceMs: 400,
    onClose: () => { priceModalOpen = false; },
  });

  const groupsHost = modal.el.querySelector<HTMLElement>('#gm-price-groups')!;
  const errEl = modal.el.querySelector<HTMLDivElement>('#gm-price-error')!;
  const saveBtn = modal.el.querySelector<HTMLButtonElement>('[data-action="save"]')!;
  const hideError = (): void => { errEl.hidden = true; };
  const showError = (msg: string): void => { errEl.hidden = false; errEl.textContent = msg; };

  // Erst das Fenster, dann laden: vorher lief der Fetch VOR dem Öffnen, in der Totzeit passierte
  // sichtbar nichts und jeder weitere Klick öffnete ein weiteres Fenster (gleicher Fehler wie beim
  // Variantenpreis-Modal). Speichern bleibt gesperrt, bis die Gruppen stehen.
  saveBtn.disabled = true;
  const loadingEl = document.createElement('div');
  loadingEl.className = 'kl-form-hint';
  loadingEl.textContent = 'Preise werden geladen …';
  groupsHost.appendChild(loadingEl);
  /** Ladeproblem IM Fenster melden statt als Toast hinter einem leeren Dialog. */
  const showLoadProblem = (msg: string): void => {
    loadingEl.textContent = 'Die Preise konnten nicht geladen werden. Bitte das Fenster schließen.';
    showError(msg);
  };

  const invoked = await callProductPrices(ctx, { action: 'get', storefront, itemId });
  if (invoked.error) { showLoadProblem('Preise nicht ladbar: ' + invoked.error.message); return; }
  const resp = invoked.data as GetResp | null;
  if (!resp || resp.channelId === null) { showLoadProblem(resp?.note ?? 'Kein Vario-Kanal für diesen Shop konfiguriert.'); return; }
  if (!resp.groups.length) { showLoadProblem('Keine zugeordneten Kundengruppen - bitte in den Einstellungen zuordnen.'); return; }
  const varioArticleId = resp.varioArticleId ?? null;

  // Gambio-Netto-Referenzwerte je (Kundengruppe, Menge) → für die „aus Gambio übernehmen"-Hilfe.
  const gambioNet = new Map<string, number>();
  for (const g of resp.gambioGroups ?? []) {
    gambioNet.set(`${g.customerGroupId}::1`, g.groupPrice);
    for (const gp of g.graduatedPrices ?? []) {
      gambioNet.set(`${g.customerGroupId}::${gp.threshold || 1}`, gp.graduatedPrice);
    }
  }

  // Editier-Modell (Quelle der Wahrheit für Save). Je Zeile Netto UND Brutto; `base` = zuletzt
  // getippte Seite (maßgeblich beim Speichern). Steuerfaktor aus echten Daten, sonst 19%.
  const factor = typeof resp.taxFactor === 'number' && resp.taxFactor > 1 ? resp.taxFactor : PRICE_VAT_FACTOR;
  const r2 = (n: number): number => Math.round(n * 100) / 100;
  interface EditTier { fromQuantity: number; net: number | null; gross: number | null; base: 'NET_PRICE' | 'GROSS_PRICE'; inherited: boolean; }
  interface EditGroup { varioPriceGroupId: string | null; varioCustomerGroupId: string | null; label: string | null; gambioStatusId: number; tiers: EditTier[]; }
  const groupTitle = (g: EditGroup): string => g.label ?? ((g.varioPriceGroupId || g.varioCustomerGroupId) ? 'Kundengruppe' : 'Standardpreis');
  const model: EditGroup[] = resp.groups.map((g) => {
    const isStd = g.varioPriceGroupId === null && g.varioCustomerGroupId === null;
    let tiers: EditTier[] = (g.tiers ?? []).map((t) => ({ fromQuantity: t.fromQuantity, net: t.net, gross: t.gross, base: t.priceBase, inherited: t.inherited ?? false }));
    // Gambio-Grundpreis ist einwertig (keine Staffeln möglich) → Standardpreis nur „Menge ab 1".
    if (isStd) tiers = tiers.filter((t) => t.fromQuantity === 1);
    // Basiszeile (Menge ab 1) sicherstellen - bei Gruppen ganz ohne Vario-Preis leer vorbelegen.
    if (!tiers.some((t) => t.fromQuantity === 1)) tiers.unshift({ fromQuantity: 1, net: null, gross: null, base: 'NET_PRICE', inherited: false });
    tiers.sort((a, b) => a.fromQuantity - b.fromQuantity);
    return { varioPriceGroupId: g.varioPriceGroupId, varioCustomerGroupId: g.varioCustomerGroupId, label: g.label ?? null, gambioStatusId: g.gambioStatusId, tiers };
  });
  loadingEl.remove();
  saveBtn.disabled = false;

  const cellStyle = 'padding:3px 6px;vertical-align:middle;';
  const headStyle = 'text-align:left;font-size:12px;color:#6b7280;font-weight:600;padding:4px 6px;';
  const inputStyle = 'width:100%;box-sizing:border-box;padding:4px 6px;border:1px solid var(--kl-border,#d8d8dc);border-radius:6px;font:inherit;';
  const mkPriceInput = (val: number | null): HTMLInputElement => {
    const el = document.createElement('input');
    el.type = 'number'; el.min = '0'; el.step = '0.01'; el.inputMode = 'decimal';
    el.placeholder = '0,00'; el.style.cssText = inputStyle;
    el.value = val !== null && Number.isFinite(val) ? String(val) : '';
    return el;
  };

  for (const group of model) {
    const isStd = group.varioPriceGroupId === null && group.varioCustomerGroupId === null; // Standardpreis: einwertig, keine Staffeln
    const section = document.createElement('div');
    section.style.cssText = 'margin-bottom:14px;padding:12px;border:1px solid var(--kl-border,#e3e3e7);border-radius:8px;';

    const heading = document.createElement('div');
    heading.style.cssText = 'display:flex;align-items:baseline;gap:10px;margin-bottom:8px;flex-wrap:wrap;';
    heading.innerHTML = `<strong>${escapeHtml(groupTitle(group))}</strong>`;
    section.appendChild(heading);
    if (isStd && group.tiers.some((t) => t.inherited)) {
      const note = document.createElement('div');
      note.className = 'kl-form-hint';
      note.style.cssText = 'font-size:12px;margin:-2px 0 8px;';
      note.textContent = 'Ausgangswert aus dem Vario-Artikel-Grundpreis. Beim Ändern wird ein Standardpreis nur für Gambio gesetzt (Artikel-Grundpreis und andere Marktplätze bleiben unberührt).';
      section.appendChild(note);
    }

    const table = document.createElement('table');
    table.style.cssText = 'width:100%;border-collapse:collapse;';
    table.innerHTML = `<thead><tr>
      <th style="${headStyle}width:96px;">Menge ab</th>
      <th style="${headStyle}">Netto (€)</th>
      <th style="${headStyle}">Brutto (€)</th>
      <th style="width:36px;"></th>
    </tr></thead>`;
    const tbody = document.createElement('tbody');
    table.appendChild(tbody);
    section.appendChild(table);

    const addBtn = document.createElement('button');
    addBtn.type = 'button';
    addBtn.className = 'tbtn';
    addBtn.style.cssText = 'margin-top:8px;';
    addBtn.textContent = '+ Staffel';
    if (!isStd) section.appendChild(addBtn); // Gambio-Grundpreis kennt keine Staffeln
    groupsHost.appendChild(section);

    const baseTier = group.tiers.find((t) => t.fromQuantity === 1)!;

    const buildRow = (tier: EditTier): HTMLTableRowElement => {
      const isBase = tier === baseTier;
      const tr = document.createElement('tr');

      // Menge-ab-Spalte (Basiszeile fix 1, nicht editier-/entfernbar).
      const qtyTd = document.createElement('td');
      qtyTd.style.cssText = cellStyle;
      const qtyInput = document.createElement('input');
      qtyInput.type = 'number';
      qtyInput.min = '1';
      qtyInput.step = '1';
      qtyInput.inputMode = 'numeric';
      qtyInput.value = String(tier.fromQuantity);
      qtyInput.style.cssText = inputStyle;
      if (isBase) {
        qtyInput.disabled = true;
        qtyInput.title = 'Grundpreis - Menge ab 1 (fest)';
      }
      qtyInput.addEventListener('input', () => {
        const n = Number.parseInt(qtyInput.value, 10);
        tier.fromQuantity = Number.isNaN(n) ? Number.NaN : n;
        hideError();
      });
      qtyTd.appendChild(qtyInput);
      tr.appendChild(qtyTd);

      // Netto- und Brutto-Eingabe, über den Steuerfaktor verlinkt (was zuletzt getippt wird, gilt).
      const netTd = document.createElement('td'); netTd.style.cssText = cellStyle;
      const grossTd = document.createElement('td'); grossTd.style.cssText = cellStyle;
      const netInput = mkPriceInput(tier.net);
      const grossInput = mkPriceInput(tier.gross);
      netTd.appendChild(netInput);
      grossTd.appendChild(grossInput);

      netInput.addEventListener('input', () => {
        const v = netInput.value.trim().replace(',', '.');
        if (v === '') { tier.net = null; tier.gross = null; grossInput.value = ''; }
        else {
          const n = Number(v); tier.net = n; tier.base = 'NET_PRICE'; tier.inherited = false;
          if (Number.isFinite(n)) { tier.gross = r2(n * factor); grossInput.value = String(tier.gross); }
        }
        hideError();
      });
      grossInput.addEventListener('input', () => {
        const v = grossInput.value.trim().replace(',', '.');
        if (v === '') { tier.gross = null; tier.net = null; netInput.value = ''; }
        else {
          const n = Number(v); tier.gross = n; tier.base = 'GROSS_PRICE'; tier.inherited = false;
          if (Number.isFinite(n)) { tier.net = r2(n / factor); netInput.value = String(tier.net); }
        }
        hideError();
      });

      // „aus Gambio übernehmen" (Gambio = netto) - nur bei gemappten Gruppen mit abweichendem Wert.
      const gNet = gambioNet.get(`${group.gambioStatusId}::${tier.fromQuantity || 1}`);
      if (typeof gNet === 'number') {
        const differs = tier.net === null || Math.abs(gNet - tier.net) > 0.005;
        if (differs) {
          const takeBtn = document.createElement('button');
          takeBtn.type = 'button';
          takeBtn.className = 'tbtn';
          takeBtn.style.cssText = 'font-size:11px;padding:1px 6px;margin-top:4px;';
          takeBtn.textContent = `aus Gambio übernehmen (netto ${fmtPrice(gNet)})`;
          takeBtn.addEventListener('click', () => {
            tier.net = r2(gNet); tier.base = 'NET_PRICE'; tier.gross = r2(gNet * factor);
            netInput.value = String(tier.net); grossInput.value = String(tier.gross);
            takeBtn.remove();
            hideError();
          });
          netTd.appendChild(takeBtn);
        }
      }

      tr.appendChild(netTd);
      tr.appendChild(grossTd);

      // Aktionsspalte: Staffel entfernen (Nicht-Basiszeile) bzw. Preis entfernen (Grundpreis-Zeile).
      const rmTd = document.createElement('td');
      rmTd.style.cssText = cellStyle + 'text-align:right;';
      if (!isBase) {
        const rmBtn = document.createElement('button');
        rmBtn.type = 'button';
        rmBtn.className = 'act-icon danger';
        rmBtn.title = 'Staffel entfernen';
        rmBtn.textContent = '✕';
        rmBtn.addEventListener('click', () => {
          const idx = group.tiers.indexOf(tier);
          if (idx >= 0) group.tiers.splice(idx, 1);
          tr.remove();
          hideError();
        });
        rmTd.appendChild(rmBtn);
      } else {
        // Grundpreis-Zeile: „Preis entfernen" leert Netto+Brutto → beim Speichern wird die
        // Vario-Zeile gelöscht und Gambio zieht für diese Kundengruppe den Standardpreis.
        const clrBtn = document.createElement('button');
        clrBtn.type = 'button';
        clrBtn.className = 'act-icon danger';
        clrBtn.title = 'Preis entfernen - Gambio nutzt dann den Standardpreis';
        clrBtn.textContent = '✕';
        clrBtn.addEventListener('click', () => {
          tier.net = null;
          tier.gross = null;
          netInput.value = '';
          grossInput.value = '';
          hideError();
        });
        rmTd.appendChild(clrBtn);
      }
      tr.appendChild(rmTd);
      return tr;
    };

    for (const tier of group.tiers) tbody.appendChild(buildRow(tier));

    if (!isStd) {
      addBtn.addEventListener('click', () => {
        const maxQty = group.tiers.reduce((m, t) => Math.max(m, Number.isFinite(t.fromQuantity) ? t.fromQuantity : 1), 1);
        const tier: EditTier = { fromQuantity: maxQty + 1, net: null, gross: null, base: 'NET_PRICE', inherited: false };
        group.tiers.push(tier);
        tbody.appendChild(buildRow(tier));
        hideError();
      });
    }
  }

  saveBtn.addEventListener('click', () => {
    // Nur ausgefüllte Staffeln (mit Preis) zählen; komplett leere Sektionen (z.B. ungenutzter
    // Standardpreis) werden übersprungen, nicht als Fehler behandelt.
    const payloadGroups: Array<{ varioPriceGroupId: string | null; varioCustomerGroupId: string | null; tiers: Array<{ fromQuantity: number; price: number; priceBase: 'NET_PRICE' | 'GROSS_PRICE' }> }> = [];
    for (const group of model) {
      const filled = group.tiers.filter((t) => {
        if (t.inherited) return false; // geerbter Artikel-Grundpreis: nur schreiben, wenn geändert
        const v = t.base === 'NET_PRICE' ? t.net : t.gross;
        return v !== null && Number.isFinite(v);
      });
      // Auch geleerte Gruppen mitschicken (tiers: []) → das Backend löscht ihre Vario-Zeilen,
      // damit Gambio für diese Kundengruppe den Standardpreis zieht.
      const seen = new Set<number>();
      for (const t of filled) {
        if (!Number.isInteger(t.fromQuantity) || t.fromQuantity < 1) {
          showError(`${groupTitle(group)}: „Menge ab" muss eine ganze Zahl ≥ 1 sein.`);
          return;
        }
        if (seen.has(t.fromQuantity)) {
          showError(`${groupTitle(group)}: „Menge ab ${t.fromQuantity}" ist doppelt vergeben.`);
          return;
        }
        seen.add(t.fromQuantity);
        if (((t.base === 'NET_PRICE' ? t.net : t.gross) as number) < 0) {
          showError(`${groupTitle(group)}: Preis darf nicht negativ sein.`);
          return;
        }
      }
      payloadGroups.push({
        varioPriceGroupId: group.varioPriceGroupId,
        varioCustomerGroupId: group.varioCustomerGroupId,
        tiers: filled
          .map((t) => ({ fromQuantity: t.fromQuantity, price: (t.base === 'NET_PRICE' ? t.net : t.gross) as number, priceBase: t.base }))
          .sort((a, b) => a.fromQuantity - b.fromQuantity),
      });
    }
    saveBtn.disabled = true;
    const prevHtml = saveBtn.innerHTML;
    saveBtn.innerHTML = '<span class="v-icon fa-regular fa-spinner-third fa-spin"></span> Speichere …';
    hideError();
    void (async () => {
      try {
        const { data, error } = await callProductPrices(ctx, { action: 'save', storefront, itemId, groups: payloadGroups });
        if (error) throw new Error(error.message);
        const out = data as PriceSaveResp | null;
        if (!out || !out.ok) {
          showError(out?.errors?.length ? out.errors.join(' · ') : 'Speichern fehlgeschlagen.');
          saveBtn.disabled = false;
          saveBtn.innerHTML = prevHtml;
          return;
        }
        // Artikelscoped Gambio-Push anstoßen (spiegelt die „Hochladen"-Aktion der Zeile).
        if (varioArticleId) {
          await ctx.supabase.rpc('gambio_enqueue_publish_or_update', { _shop_key: storefront, _vario_article_ids: [varioArticleId] });
        }
        showToast('Preise gespeichert, Übertragung nach Gambio angestoßen.', 'success');
        modal.close();
        onSaved();
      } catch (ex) {
        openErrorModal({
          title: 'Preise speichern fehlgeschlagen',
          items: [{ message: (ex as Error).message }],
          context: { action: 'gambio-product-prices:save', storefront, itemId },
          json: { message: (ex as Error).message, stack: (ex as Error).stack },
        });
        saveBtn.disabled = false;
        saveBtn.innerHTML = prevHtml;
      }
    })();
  });
}

// Antwort-Ausschnitt von gambio-variant-rows (action:'list'), nur die hier genutzten Felder.
interface VariantListChild { articleId: string; priceEur: number | null; priceLoadFailed?: boolean }
interface VariantListResp {
  rows: VariantListChild[];
  parentPriceEur?: number | null;
  /** true = der Preis-Read gegen Vario ist fehlgeschlagen (nicht: es gibt keinen Preis). */
  parentPriceLoadFailed?: boolean;
  priceMode?: string;
}
interface VariantSaveResp { results: Array<{ articleId: string; price?: { ok: boolean; message?: string } }>; }

/**
 * Genau EIN Variantenpreis-Modal gleichzeitig. Ohne diese Sperre öffnete jeder weitere Klick auf
 * die Preis-Zelle ein zusätzliches Fenster (der Ladevorgang dauert, es passiert sichtbar nichts,
 * also klickt man nochmal) — vier Klicks ergaben vier kaskadierte Modale, und „Abbrechen" schloss
 * jeweils nur das oberste. Wird im `onClose` des Modals zurückgesetzt.
 */
let variantPriceModalOpen = false;

/** Preis-Modal für EINE Variante (Auf-/Abschlag + Endpreis brutto, Zwei-Wege-Bindung über den
 *  Vaterpreis). Reuse des Variantenansicht-Backends: list(listingId) → Kind filtern; save schreibt
 *  Vario-Basispreis + stößt den Gambio-Sync an. listingId = varioListingId der Eltern-Zeile.
 *
 *  Das Fenster geht SOFORT auf und lädt darin nach (Felder gesperrt, „wird geladen"), statt erst
 *  nach der Antwort zu erscheinen. Ein Ladefehler wird IM Fenster gemeldet, nicht als zweites
 *  Fenster darüber. */
async function openVariantPriceModal(
  ctx: ProductsCtx, row: GridRow, parentListingId: string, onSaved: () => void,
): Promise<void> {
  if (variantPriceModalOpen) return; // Klick-Sperre gegen Mehrfach-Öffnen
  if (!row.varioArticleId) { showToast('Variante nicht mit VARIO verknüpft.', 'error'); return; }
  const varioArticleId = row.varioArticleId; // lokale const: bleibt in den Closures narrowed
  const r2 = (n: number): number => Math.round(n * 100) / 100;

  const body = `
    <div id="gm-vp-mode-warn"></div>
    <div class="kl-form-hint" id="gm-vp-hint" style="margin-bottom:12px;">Preis wird geladen …</div>
    <div style="display:grid;grid-template-columns:auto 1fr;gap:8px 12px;align-items:center;max-width:360px;">
      <label for="gm-vp-surcharge">Auf-/Abschlag (€)</label>
      <input id="gm-vp-surcharge" type="number" step="0.01" inputmode="decimal" placeholder="0,00" disabled
        style="width:100%;box-sizing:border-box;padding:4px 6px;border:1px solid var(--kl-border,#d8d8dc);border-radius:6px;font:inherit;" />
      <label for="gm-vp-end">Endpreis (brutto, €)</label>
      <input id="gm-vp-end" type="number" step="0.01" inputmode="decimal" placeholder="0,00" disabled
        style="width:100%;box-sizing:border-box;padding:4px 6px;border:1px solid var(--kl-border,#d8d8dc);border-radius:6px;font:inherit;" />
    </div>
    <div class="kl-modal-error" id="gm-vp-error" hidden style="margin-top:10px;"></div>
  `;
  const footer = `
    <button type="button" class="v-button" data-modal-cancel>Abbrechen</button>
    <button type="button" class="v-button v-button--save" data-action="save" disabled><span class="v-icon fa-regular fa-check"></span> Preis speichern</button>
  `;
  variantPriceModalOpen = true;
  const modal = openModal({
    title: 'Variantenpreis bearbeiten', icon: 'fa-tags', width: 480, body, footer,
    // Schonfrist: der zweite Klick eines Doppelklicks trifft sonst das Backdrop und schliesst sofort.
    dismissGraceMs: 400,
    onClose: () => { variantPriceModalOpen = false; },
  });
  const warnEl = modal.el.querySelector<HTMLDivElement>('#gm-vp-mode-warn')!;
  const hintEl = modal.el.querySelector<HTMLDivElement>('#gm-vp-hint')!;
  const surchargeEl = modal.el.querySelector<HTMLInputElement>('#gm-vp-surcharge')!;
  const endEl = modal.el.querySelector<HTMLInputElement>('#gm-vp-end')!;
  const errEl = modal.el.querySelector<HTMLDivElement>('#gm-vp-error')!;
  const saveBtn = modal.el.querySelector<HTMLButtonElement>('[data-action="save"]')!;
  const parseNum = (s: string): number | null => { const t = s.trim().replace(',', '.'); if (t === '') return null; const n = Number(t); return Number.isFinite(n) ? n : null; };

  const invoked = await callVariantRows(ctx, { action: 'list', listingId: parentListingId });
  if (invoked.error) {
    // Fehler IM Fenster: ein zweites Modal darüber war genau das gemeldete Ärgernis.
    hintEl.textContent = 'Der Preis konnte nicht geladen werden. Bitte das Fenster schließen und erneut versuchen.';
    errEl.hidden = false;
    errEl.textContent = invoked.error.message;
    return;
  }
  const resp = invoked.data as VariantListResp | null;
  const child = (resp?.rows ?? []).find((r) => r.articleId === varioArticleId);
  const parentGross = typeof resp?.parentPriceEur === 'number' ? resp.parentPriceEur : null;
  const priceMode = resp?.priceMode ?? 'fixed';
  const childGross = child && typeof child.priceEur === 'number' ? child.priceEur : null;

  // Preis-Read gescheitert: NICHT vorbelegen. Ein stiller Rueckfall auf den Vaterpreis sah wie ein
  // echter Wert aus, und beim Speichern haette man ihn als Variantenpreis nach Vario geschrieben.
  if (child?.priceLoadFailed === true || resp?.parentPriceLoadFailed === true) {
    hintEl.textContent = 'Der Preis konnte in Vario nicht gelesen werden. Das Fenster bleibt gesperrt, damit kein Ersatzwert gespeichert wird. Bitte schließen und erneut versuchen.';
    errEl.hidden = false;
    errEl.textContent = 'Vario-Preisabfrage fehlgeschlagen.';
    return;
  }

  if (priceMode !== 'from_options') {
    warnEl.innerHTML = `<div class="v-banner v-banner-warn" style="margin-bottom:12px;"><span class="v-icon fa-regular fa-triangle-exclamation"></span><div>Der Verkaufskanal steht auf „Aufschlag". Für einen Abschlag (Endpreis unter dem Vaterpreis) in den Einstellungen auf „Endpreis (from_options)" umstellen.</div></div>`;
  }
  // Kein eigener Variantenpreis: die Variante folgt dem Vaterpreis (gewolltes Verhalten). Das wird
  // ausgeschrieben, damit der vorbelegte Wert nicht wie ein eigener Preis der Variante aussieht.
  const followsParent = childGross == null && parentGross != null;
  hintEl.innerHTML = `Vaterpreis (brutto): <strong>${fmtPrice(parentGross)}</strong>.${followsParent ? ' Die Variante hat noch keinen eigenen Preis und folgt dem Vaterpreis.' : ''} Auf-/Abschlag und Endpreis sind verbunden — die jeweils andere Seite wird berechnet. Der Endpreis wird als Vario-Basispreis der Variante gespeichert und an Gambio übertragen.`;

  const initialEnd = childGross ?? parentGross;
  if (initialEnd != null) endEl.value = String(r2(initialEnd));
  if (initialEnd != null && parentGross != null) surchargeEl.value = String(r2(initialEnd - parentGross));
  surchargeEl.disabled = false;
  endEl.disabled = false;
  saveBtn.disabled = false;

  // Zwei-Wege-Bindung über den Vaterpreis (nur wenn Vater bekannt).
  surchargeEl.addEventListener('input', () => {
    if (parentGross == null) return;
    const s = parseNum(surchargeEl.value);
    endEl.value = s == null ? '' : String(r2(parentGross + s));
  });
  endEl.addEventListener('input', () => {
    if (parentGross == null) return;
    const p = parseNum(endEl.value);
    surchargeEl.value = p == null ? '' : String(r2(p - parentGross));
  });

  saveBtn.addEventListener('click', () => {
    errEl.hidden = true;
    const end = parseNum(endEl.value);
    if (end == null || !(end > 0)) { errEl.hidden = false; errEl.textContent = 'Endpreis muss größer als 0 sein.'; return; }
    saveBtn.setAttribute('disabled', '');
    void (async () => {
      const { data, error } = await callVariantRows(ctx, { action: 'save', listingId: parentListingId, rows: [{ articleId: varioArticleId, priceEur: end }] });
      if (error) {
        saveBtn.removeAttribute('disabled');
        openErrorModal({
          title: 'Variantenpreis konnte nicht gespeichert werden',
          items: [{ message: error.message }],
          context: { action: 'gambio-variant-rows:save', storefront: row.storefront, idItem: row.idItem },
          json: error.json ?? error,
        });
        return;
      }
      const res = data as VariantSaveResp | null;
      const pr = res?.results?.find((x) => x.articleId === varioArticleId)?.price;
      if (pr && pr.ok === false) {
        saveBtn.removeAttribute('disabled');
        openErrorModal({
          title: 'Variantenpreis konnte nicht gespeichert werden',
          items: [{ message: pr.message ?? 'Speichern fehlgeschlagen.' }],
          context: { action: 'gambio-variant-rows:save', storefront: row.storefront, idItem: row.idItem },
          json: data,
        });
        return;
      }
      modal.close();
      showToast('Variantenpreis gespeichert — Übertragung an Gambio angestoßen.', 'success');
      onSaved();
    })();
  });
}

/**
 * Bulk-Import: Kundengruppen-/Staffelpreise für die markierten Zeilen aus Gambio nach Vario
 * übernehmen (Edge Function `gambio-product-prices`, action:'import'). Varianten und nicht
 * gemappte Zeilen werden serverseitig übersprungen - hier vorab schon rausgefiltert, damit die
 * Fehler-/Erfolgs-Zählung nur importierbare Zeilen betrifft. Gruppiert nach Storefront, da die
 * Edge Function pro Shop aufgerufen wird.
 */
async function importPricesFromGambio(ctx: ProductsCtx, rows: GridRow[], reload: () => void): Promise<void> {
  const byStore = new Map<string, string[]>();
  for (const r of rows) {
    if (r.isVariant || !r.varioArticleId) continue;
    const arr = byStore.get(r.storefront) ?? []; arr.push(r.idItem); byStore.set(r.storefront, arr);
  }
  if (byStore.size === 0) { showToast('Keine importierbaren (nicht-Varianten, gemappten) Zeilen ausgewählt.', 'error'); return; }
  showToast('Preise werden aus Gambio importiert …', 'info');
  let ok = 0, failed = 0;
  for (const [storefront, itemIds] of byStore) {
    const { data, error } = await callProductPrices(ctx, { action: 'import', storefront, itemIds });
    if (error) { failed += itemIds.length; continue; }
    for (const r of ((data as { results?: Array<{ ok: boolean }> })?.results ?? [])) { if (r.ok) ok += 1; else failed += 1; }
  }
  showToast(`Import fertig: ${ok} ok, ${failed} Fehler.`, failed ? 'error' : 'success');
  reload();
}

export async function renderProductsTab(host: HTMLElement, ctx: ProductsCtx, opts?: unknown): Promise<void> {
  // Dritter Parameter ist `unknown` (Tab.render-Signatur). Das /#/products-Verkaufskanal-Fenster
  // reicht { headerHtml } durch (Mock-Header ohne Menü); im Dashboard-Tab leer.
  const headerHtml =
    opts && typeof opts === 'object' && 'headerHtml' in opts
      ? String((opts as { headerHtml?: string }).headerHtml ?? '')
      : '';
  host.classList.add('go-products');
  host.innerHTML = `
    ${headerHtml}
    <div class="filterbar">
      <div class="select">
        <select id="gambio-products-status" title="Status filtern">
          <option value="">Alle Status</option>
          <option value="active">Aktiv</option>
          <option value="inactive">Inaktiv</option>
        </select>
      </div>
      <div class="select">
        <select id="gambio-products-mapping" title="Mapping filtern">
          <option value="">Alle Mappings</option>
          <option value="mapped">Zugeordnet</option>
          <option value="unmapped">Nicht zugeordnet</option>
          <option value="in-vario">In Vario vorhanden, nicht zugeordnet</option>
        </select>
      </div>
      <div class="tbsearch grow">
        ${SVG_SEARCH}
        <input id="gambio-products-search" type="search" placeholder="SKU, EAN oder Titel suchen…" autocomplete="off" />
      </div>
      <button type="button" class="infobtn" id="gambio-products-legend-btn" title="Legende" aria-label="Legende" aria-expanded="false">${SVG_INFO}</button>
      <span class="tb-count" id="gambio-products-count"></span>
      <button type="button" class="tbtn" id="gambio-products-selall" hidden
        title="Alle Artikel auswählen, die zum aktuellen Filter passen - auch die, die noch nicht geladen sind"></button>
      <button type="button" class="tbtn primary" id="gambio-products-refresh" title="Nur seit dem letzten Abgleich geänderte Produkte aus Gambio laden & per SKU mit Vario abgleichen (schnell)">
        <span class="v-icon fa-regular fa-rotate-right" id="gambio-products-refresh-icon"></span> Produkte aktualisieren
      </button>
      <button type="button" class="tbtn" id="gambio-products-full-refresh" title="Vollständig abgleichen (auch gelöschte Produkte aufräumen, langsamer)">
        <span class="v-icon fa-regular fa-rotate-right" id="gambio-products-full-refresh-icon"></span> Vollständig abgleichen
      </button>
    </div>

    <div class="bulkbar" id="gambio-products-bulkbar">
      <span class="selinfo"><span class="cnt" id="gambio-products-selcnt">0</span>ausgewählt<span id="gambio-products-selall-note" hidden> — alle Treffer des Filters</span></span>
      <button type="button" class="tbtn" id="gambio-products-bulk-assign" title="Markierte Artikel mit passenden VARIO-Artikeln verknüpfen">${SVG_LINK}Verknüpfen</button>
      <button type="button" class="tbtn" id="gambio-products-bulk-seed" title="Für die markierten Artikel neue VARIO-Artikel erzeugen">${SVG_PLUS}In VARIO anlegen</button>
      <button type="button" class="tbtn" id="gambio-products-bulk-minmax" title="Min/Max-Bestand für die Auswahl setzen">${SVG_MINMAX}Min/Max-Bestand</button>
      <button type="button" class="tbtn" id="gambio-products-bulk-download" title="Markierte Gambio-Artikel nach Vario aktualisieren (Kanal-Daten G→V)">${SVG_DOWNLOAD}Herunterladen</button>
      <button type="button" class="tbtn" id="gambio-products-bulk-create-variants" title="Für die markierten zugeordneten Vario-Artikel Varianten in Gambio anlegen">${SVG_PLUS}Varianten zu Gambio anlegen</button>
      <button type="button" class="tbtn" id="gambio-products-bulk-import-prices" title="Kundengruppen-/Staffelpreise der markierten zugeordneten Artikel aus Gambio nach Vario importieren">${SVG_DOWNLOAD}Preise aus Gambio importieren</button>
      <button type="button" class="tbtn" id="gambio-products-export" title="Bestand, Preis, Sonderpreis und Bilder ALLER zugeordneten Artikel aus Vario nach Gambio exportieren (Auswahl-unabhängig)"><span class="v-icon fa-regular fa-cloud-arrow-up" id="gambio-products-export-icon"></span>Export zu Gambio</button>
      <button type="button" class="clear" id="gambio-products-bulk-clear">Auswahl aufheben</button>
    </div>

    <div class="card">
      <div class="grid-wrap"><div id="gambio-products-grid" class="ag-theme-vario ag-theme-quartz"></div></div>
    </div>

    <div class="legend" id="gambio-products-legend" hidden>
      <div class="lg-sec">
        <div class="lg-h">Zeilen-Farben</div>
        <div class="lg-row"><span class="sw" style="background:var(--child)"></span><div><b>Hellblau</b><span>Variante eines Variantenhauptartikels</span></div></div>
        <div class="lg-row"><span class="sw" style="background:#fff;border:1px solid #D8D8DC"></span><div><b>Weiß</b><span>Haupt- oder Flachartikel</span></div></div>
      </div>
      <div class="lg-sec">
        <div class="lg-h">Markierte Zeilen (Auswahl)</div>
        <div class="lg-row"><span class="sw" style="background:#EDF6DC;box-shadow:inset 3px 0 0 var(--vario-green)"></span><div><b>Grün</b><span>ausgewählte zugeordnete Zeile</span></div></div>
        <div class="lg-row"><span class="sw" style="background:#ECECEF;box-shadow:inset 3px 0 0 #b6bcc4"></span><div><b>Grau</b><span>ausgewählte nicht zugeordnete Zeile</span></div></div>
      </div>
      <div class="lg-sec">
        <div class="lg-h">Status-Markierungen</div>
        <div class="lg-row"><span class="lg-st"><span class="pd" style="background:var(--vario-green)"></span>Aktiv</span><div><span>Im Gambio-Shop aktiv - Schalter in „Gambio-Status" klicken zum Umschalten</span></div></div>
        <div class="lg-row"><span class="lg-st"><span class="pd" style="background:#b6bcc4"></span>Inaktiv</span><div><span>Im Gambio-Shop inaktiv (nicht sichtbar) - Schalter klicken zum Aktivieren</span></div></div>
        <div class="lg-row"><span class="lg-st"><span class="pd" style="background:var(--vario-green)"></span>Vario-Nr.</span><div><span>Vario-Artikel zugeordnet (Spalte „Vario SKU")</span></div></div>
        <div class="lg-row"><span class="lg-st" style="color:#9A6A12"><span class="pd" style="background:var(--warn)"></span>nicht zugeordnet</span><div><span>Kein VARIO-Artikel verknüpft</span></div></div>
        <div class="lg-row"><span class="lg-st lg-dash">—</span><div><span>Kein Wert - z. B. Varianten tragen kein Gambio-Aktiv-Flag</span></div></div>
      </div>
      <div class="lg-sec">
        <div class="lg-h">Artikelart</div>
        <div class="lg-row"><span class="lg-st" style="color:var(--vario-green-dark)"><span style="display:flex;width:14px;height:14px;">${SVG_ART_MAIN}</span>Variantenhauptartikel</span><div><span>Bestandslos, trägt die Varianten</span></div></div>
        <div class="lg-row"><span class="lg-st" style="color:#9aa0a6"><span style="display:flex;width:14px;height:14px;">${SVG_ART_VARIANT}</span>Variante</span><div><span>Artikel mit Bestand</span></div></div>
      </div>
    </div>
  `;
  const gridHost = host.querySelector<HTMLElement>('#gambio-products-grid')!;
  const refreshBtn = host.querySelector<HTMLButtonElement>('#gambio-products-refresh')!;
  const refreshIcon = host.querySelector<HTMLElement>('#gambio-products-refresh-icon')!;
  const fullRefreshBtn = host.querySelector<HTMLButtonElement>('#gambio-products-full-refresh')!;
  const fullRefreshIcon = host.querySelector<HTMLElement>('#gambio-products-full-refresh-icon')!;
  const exportBtn = host.querySelector<HTMLButtonElement>('#gambio-products-export')!;
  const exportIcon = host.querySelector<HTMLElement>('#gambio-products-export-icon')!;
  const countEl = host.querySelector<HTMLElement>('#gambio-products-count')!;
  // Bulk-Leiste (nur bei Auswahl sichtbar). Variablen-Namen wie zuvor (assignBtn/seedBtn/…),
  // damit die unveränderten Aktions-Funktionen weiter darauf zugreifen.
  const bulkbar = host.querySelector<HTMLElement>('#gambio-products-bulkbar')!;
  const selCnt = host.querySelector<HTMLElement>('#gambio-products-selcnt')!;
  const assignBtn = host.querySelector<HTMLButtonElement>('#gambio-products-bulk-assign')!;
  const seedBtn = host.querySelector<HTMLButtonElement>('#gambio-products-bulk-seed')!;
  const minmaxBtn = host.querySelector<HTMLButtonElement>('#gambio-products-bulk-minmax')!;
  const updateFromGambioBtn = host.querySelector<HTMLButtonElement>('#gambio-products-bulk-download')!;
  const createVariantsBtn = host.querySelector<HTMLButtonElement>('#gambio-products-bulk-create-variants')!;
  const importPricesBtn = host.querySelector<HTMLButtonElement>('#gambio-products-bulk-import-prices')!;
  const bulkClearBtn = host.querySelector<HTMLButtonElement>('#gambio-products-bulk-clear')!;
  const selAllBtn = host.querySelector<HTMLButtonElement>('#gambio-products-selall')!;
  const selAllNote = host.querySelector<HTMLElement>('#gambio-products-selall-note')!;

  // Zweistufige Bestätigung für „Verknüpfung auflösen": speichert den
  // zusammengesetzten Schlüssel „storefront::idItem" der Zeile, die gerade im
  // Bestätigungszustand ist. Kein window.confirm/alert (VERBOTEN).
  const confirmingUnassign = new Set<string>();

  // In-Flight-Guard fuer den isActive-Toggle: Schluessel "storefront::idItem" der Zeilen,
  // deren Umschaltung gerade laeuft (Switch disabled im Re-Render - kein Doppelklick-Race).
  const togglingActive = new Set<string>();

  // Aktiv-Status je Gambio-PRODUKT (Schlüssel "storefront::productId"), nur Zeilen mit echtem Flag
  // (Flach-Produkte + Variantenhauptartikel; Kinder tragen kein isActive). Dient dazu, den
  // Frontend-Deeplink der Varianten-KINDER am Aktiv-Status ihres ELTERNprodukts auszurichten —
  // der Link zeigt bei allen Varianten auf die Elternprodukt-Seite, die bei inaktivem Vater nicht existiert.

  // shop_key → Gambio-Shop-Basis-URL (aus gambio_list_shops). Für die Deeplinks in die
  // Shop-Frontend-/Backend-Artikelseiten. Wird in load() befüllt.
  const shopBaseByKey = new Map<string, string>();
  // shop_key → virtueller Lagerbestand (Default 9999). Für die abgeleitete „Bestand"-Anzeige
  // bestandsloser Artikel (= max_bestand ?? virtueller Bestand), die der push_stock-Worker hält.
  const virtualStockByShop = new Map<string, number>();
  const DEFAULT_VIRTUAL_STOCK = 9999;
  // shop_key → sync_direction (Default 'vario_to_gambio'). Für die Bestand-„Push steht aus"-Markierung:
  // bei 'gambio_to_vario' exportiert der automatische Abgleich NICHT → Zelle bleibt weiß.
  const syncDirectionByShop = new Map<string, string>();
  // shop_key → { Gambio-Steuerklassen-ID → Inlands-Satz in % }. Für die Brutto-Anzeige der
  // Preisspalte (Netto × Satz). Best-effort je Shop in load() befüllt; leer → Anzeige bleibt Netto.
  const taxRatesByShop = new Map<string, Record<string, number>>();
  // Bestandslose Vario-Artikelarten (kein echter Bestand → Gambio-Menge = max ?? virtueller Wert).
  const STOCKLESS_TYPES = new Set(['WITHOUT_STOCK', 'SERVICE_ARTICLE']);

  /**
   * Effektiver Anzeige-„Bestand" einer Zeile. Bestandslose, gemappte Artikel führen in Gambio
   * keinen echten Bestand - der Wert wird vom Connector gehalten (= max_bestand ?? virtueller
   * Shop-Bestand). Diese Ableitung gibt SOFORTIGES Feedback bei Min/Max-Änderungen (kein Cache-/
   * Worker-Lag). Alle anderen Zeilen zeigen die gecachte Gambio-Menge.
   */
  function effectiveStock(row: GridRow): number | null {
    if (row.isVariantMain) return null; // Hauptartikel ist nicht bestandsgeführt → kein eigener Bestand
    if (row.varioArticleNumber && STOCKLESS_TYPES.has(row.varioProductType ?? '')) {
      const virt = virtualStockByShop.get(row.storefront) ?? DEFAULT_VIRTUAL_STOCK;
      return row.maxBestand ?? virt;
    }
    return row.quantity;
  }

  function unassignRowKey(row: GridRow): string {
    return `${row.storefront}::${row.idItem}`;
  }

  /**
   * Setter für Min/Max-Bestand-Spalten. Kein Pending-Pattern, da die Werte
   * NICHT an Gambio gepusht werden, sondern nur als Setting in vario.article_mappings
   * liegen. Persistierung direkt per RPC gambio_set_listing_min_max.
   * Bei Fehler: lokalen Wert reverten, refreshCells, showToast error.
   */
  function makeMinMaxSetter(which: 'min' | 'max') {
    return (params: ValueSetterParams<GridRow>): boolean => {
      if (!params.data) return false;
      const row = params.data;

      // Wert parsen: leer/null/'—' → null; sonst parseInt ≥ 0; NaN/<0 → abbrechen.
      let parsed: number | null;
      if (
        params.newValue === null ||
        params.newValue === undefined ||
        params.newValue === '' ||
        params.newValue === '—'
      ) {
        parsed = null;
      } else {
        const n =
          typeof params.newValue === 'number'
            ? params.newValue
            : Number.parseInt(String(params.newValue), 10);
        if (Number.isNaN(n) || n < 0) {
          showToast(`Ungültiger Wert - bitte eine ganze Zahl ≥ 0 eingeben.`, 'error');
          return false;
        }
        parsed = Math.trunc(n);
      }

      // Cross-Field-Prüfung: wenn beide gesetzt und min>max → abbrechen.
      const newMin = which === 'min' ? parsed : row.minBestand;
      const newMax = which === 'max' ? parsed : row.maxBestand;
      if (newMin !== null && newMax !== null && newMin > newMax) {
        showToast(`Min (${newMin}) darf nicht größer als Max (${newMax}) sein.`, 'error');
        return false;
      }

      // Optimistisch lokal updaten.
      const oldValue = which === 'min' ? row.minBestand : row.maxBestand;
      if (which === 'min') {
        row.minBestand = parsed;
      } else {
        row.maxBestand = parsed;
      }
      // „Bestand"-Spalte (abgeleitet bei bestandslosen Artikeln) sofort neu rechnen - kein Lag.
      if (params.node) api.refreshCells({ rowNodes: [params.node], columns: ['quantity'], force: true });

      // RPC fire-and-forget mit Rollback bei Fehler.
      // Beide Werte mitschicken (RPC schreibt UPDATE auf beide Spalten, kein COALESCE).
      const minVal = which === 'min' ? parsed : row.minBestand;
      const maxVal = which === 'max' ? parsed : row.maxBestand;

      void (async () => {
        const { error } = await ctx.supabase.rpc('gambio_set_listing_min_max', {
          _storefront: row.storefront,
          _id_item: row.idItem,
          _min: minVal,
          _max: maxVal,
        });
        if (error) {
          // Revert + User-Hinweis.
          if (which === 'min') {
            row.minBestand = oldValue;
          } else {
            row.maxBestand = oldValue;
          }
          if (params.node) api.refreshCells({ rowNodes: [params.node], force: true });
          openErrorModal({
            title: `${which === 'min' ? 'Min' : 'Max'}-Bestand konnte nicht gespeichert werden`,
            items: [{ message: error.message }],
            context: { action: 'gambio_set_listing_min_max', storefront: row.storefront, idItem: row.idItem, which },
            json: error,
          });
        } else {
          showToast('Min/Max gespeichert.', 'success');
        }
      })();

      return true;
    };
  }

  /**
   * Setter für die EAN-Spalte (Väter + Kinder). Schreibt die EAN artikel-global nach Vario
   * (Edge set-ean) und stößt den Gambio-Push an: Kind → gambio_enqueue_variant_field_sync
   * (mit Eltern-/Hauptartikel-ID), Vater/Einzel → gambio_enqueue_publish_or_update.
   * Optimistisch lokal setzen; bei Fehler reverten + Toast.
   */
  function makeEanSetter() {
    return (params: ValueSetterParams<GridRow>): boolean => {
      if (!params.data) return false;
      const row = params.data;
      if (!row.varioArticleId) { showToast('Nur zugeordnete Artikel haben eine editierbare EAN.', 'error'); return false; }
      const varioArticleId = row.varioArticleId; // lokale const: bleibt in der async-Closure als `string` narrowed (row ist mutable)
      const raw = params.newValue == null ? '' : String(params.newValue).trim();
      const oldValue = row.ean;
      if (raw === (oldValue ?? '')) return false; // unverändert (auch leer→leer) → nichts tun
      // Leeres Feld bei vorhandener EAN = LÖSCHEN; sonst Format prüfen (GS1-Prüfziffer serverseitig).
      const isDelete = raw === '';
      if (!isDelete && !/^\d{8}$|^\d{12,14}$/.test(raw)) { showToast('EAN muss 8, 12, 13 oder 14 Ziffern haben.', 'error'); return false; }

      // Optimistisch setzen (bei Löschen auf null).
      row.ean = isDelete ? null : raw;
      if (params.node) api.refreshCells({ rowNodes: [params.node], columns: ['ean'], force: true });

      void (async () => {
        const { error } = await callProductPrices(ctx, { action: 'set-ean', storefront: row.storefront, itemId: row.idItem, ean: raw });
        if (error) {
          row.ean = oldValue;
          if (params.node) api.refreshCells({ rowNodes: [params.node], columns: ['ean'], force: true });
          openErrorModal({ title: isDelete ? 'EAN konnte nicht entfernt werden' : 'EAN konnte nicht gespeichert werden', items: [{ message: error.message }], context: { action: 'set-ean', storefront: row.storefront, idItem: row.idItem }, json: error.json ?? error });
          return;
        }
        // Gambio-Push je Zeilentyp (best-effort). `pushed` hält fest, ob wirklich enqueued wurde,
        // damit der Erfolgstoast keine Übertragung behauptet, die (Kind ohne auffindbaren
        // Hauptartikel bzw. RPC-Fehler) gar nicht angestoßen wurde.
        let pushed = false;
        try {
          if (row.isVariant) {
            // Kommt aus der Sicht (Migration 0346); frueher eine Suche ueber ALLE Zeilen, die
            // beim seitenweisen Laden ins Leere gelaufen waere.
            const mainArticleId = row.parentVarioArticleId;
            if (mainArticleId) { await ctx.supabase.rpc('gambio_enqueue_variant_field_sync', { _shop_key: row.storefront, _vario_main_article_id: mainArticleId }); pushed = true; }
          } else {
            await ctx.supabase.rpc('gambio_enqueue_publish_or_update', { _shop_key: row.storefront, _vario_article_ids: [varioArticleId] }); pushed = true;
          }
        } catch (e) {
          console.warn('[products] EAN-Push enqueue failed', (e as Error).message);
        }
        const verb = isDelete ? 'entfernt' : 'gespeichert';
        showToast(pushed ? `EAN ${verb} — Übertragung an Gambio angestoßen.` : `EAN in Vario ${verb} — Gambio-Übertragung folgt beim nächsten Abgleich.`, 'success');
      })();
      return true;
    };
  }

  const editableMinMax = (p: { data?: GridRow | undefined }): boolean => !!p.data?.varioArticleNumber && !p.data?.isVariantMain;

  /**
   * Nennt die Variantenfamilie eines Kindes, solange die Liste NICHT in Familien-Reihenfolge steht.
   * In Familien-Reihenfolge steht das Kind direkt unter seinem Hauptartikel, da genuegt die
   * Einrueckung. Danach nicht mehr: dort sagt die Position nichts, und die Einrueckung allein liest
   * sich als Zugehoerigkeit zur Zeile darueber.
   */
  const familienHinweis = (row: GridRow): string => {
    if (familienSortierung || !row.parentModel) return '';
    return `<span class="st" style="margin-left:6px;color:var(--muted);">von ${escapeHtml(row.parentModel)}</span>`;
  };

  const cols: ColDef<GridRow>[] = [
    {
      headerName: 'Vario SKU', field: 'varioArticleNumber', width: 188,
      cellRenderer: (p: ICellRendererParams<GridRow>) => {
        if (!p.data) return '';
        const num = p.data.varioArticleNumber;
        if (!num) {
          // Variantenhauptartikel ist ein Container ohne eigenen Vario-Artikel — die Kinder sind
          // einzeln zugeordnet. Neutral kennzeichnen (nicht als „nicht zugeordnet"-Warnung, die
          // fälschlich fehlende Arbeit suggeriert). Baum-Icon wie bei gemappten Hauptartikeln.
          if (p.data.isVariantMain) {
            const n = p.data.variantCount ?? 0;
            const icon = `<span class="arttype" title="Variantenprodukt (Shop) — die ${n} ${n === 1 ? 'Variante ist' : 'Varianten sind'} einzeln einem VARIO-Artikel verknüpft">${SVG_ART_MAIN}</span>`;
            // Link auf den VARIO-Variantenhauptartikel (geteilte Stammdaten der Varianten), sofern bekannt
            // (aus variant_groups). Ohne Id: neutraler Hinweis ohne Link.
            const mainUrl = p.data.varioMainArticleId ? varioArticleUrl(p.data.varioMainArticleId) : null;
            const label = p.data.parentModel ? escapeHtml(p.data.parentModel) : 'Variantenhauptartikel';
            const inner = mainUrl
              ? `<a class="skulink" href="${escapeHtml(mainUrl)}" target="_blank" rel="noopener" title="VARIO-Variantenhauptartikel öffnen (geteilte Stammdaten der Varianten)">${label}</a>`
              : `<span class="st" style="color:var(--muted);">Variantenhauptartikel · Varianten verknüpft</span>`;
            return `<span class="sku-cell parent">${icon}${inner}</span>`;
          }
          return `<span class="st" style="color:#9A6A12;"><span class="pd" style="background:var(--warn);"></span>nicht zugeordnet</span>`;
        }
        const label = escapeHtml(num);
        // Der Artikel ist in VARIO nicht mehr auffindbar (Reconcile-Lauf, Migration 0360/0361).
        // Ohne diese Kennzeichnung stand hier eine ganz normale Zeile mit einer Nummer, die es in
        // VARIO nicht mehr gibt — genau der Zustand, ueber den der Betreiber gestolpert ist. Kein
        // Deeplink: er fuehrt ins Leere.
        if (p.data.varioArticleMissingSince) {
          // Der Text darf NICHT „gelöscht" behaupten. Die Spalte sagt nur, dass der letzte Abgleich
          // den Artikel nicht gefunden hat — beim ersten Strike ist das eine unbestätigte Vermutung,
          // deshalb steht die Zuordnung ja noch. Und ist die Löschung bestätigt, FÄLLT die Zuordnung
          // und die Zeile zeigt „nicht zugeordnet". Das Etikett erscheint also ausschließlich in dem
          // Fenster, in dem die Löschung gerade NICHT feststeht: „gelöscht" wäre in jedem sichtbaren
          // Fall zu viel behauptet.
          return `<span class="sku-cell"><span class="st" style="color:#9A6A12;" title="Der VARIO-Artikel war beim letzten Abgleich nicht auffindbar. Der nächste Abgleich prüft erneut: ist er wieder da, verschwindet der Hinweis. Ist er wirklich gelöscht, wird die Zuordnung entfernt; danach legt „In VARIO anlegen“ einen neuen Artikel an - sofern das Produkt im Shop aktiv ist.">`
            + `<span class="pd" style="background:var(--warn);"></span>${label} · in VARIO nicht gefunden</span></span>`;
        }
        // Deeplink direkt in den Vario-Verkaufskanal (Kanal-Listing); fehlt die Listing-ID, Fallback auf die Artikel-Detailseite.
        const url = varioListingUrl(p.data.varioArticleId, p.data.varioListingId) ?? varioArticleUrl(p.data.varioArticleId);
        const link = url
          ? `<a class="skulink" href="${escapeHtml(url)}" target="_blank" rel="noopener" title="Im Vario-Verkaufskanal öffnen">${label}</a>`
          : `<span class="skulink" style="cursor:default;">${label}</span>`;
        // Baum-Look wie bei der Gambio-SKU: Hauptartikel fett + Verzweigungs-Icon, Varianten eingerückt.
        if (p.data.isVariantMain) {
          const n = p.data.variantCount ?? 0;
          return `<span class="sku-cell parent"><span class="arttype" title="Variantenhauptartikel (bestandslos) - ${n} ${n === 1 ? 'Variante' : 'Varianten'}">${SVG_ART_MAIN}</span>${link}</span>`;
        }
        if (p.data.isVariant) {
          const fam = p.data.parentModel ? ` von ${escapeHtml(p.data.parentModel)}` : '';
          return `<span class="sku-cell child"><span class="arttype" title="Artikel mit Bestand · Variante${fam}">${SVG_ART_VARIANT}</span>${link}${familienHinweis(p.data)}</span>`;
        }
        return `<span class="sku-cell"><span class="arttype" title="Artikel mit Bestand">${SVG_ART_VARIANT}</span>${link}</span>`;
      },
    },
    {
      headerName: 'Gambio SKU', field: 'sku', width: 188,
      cellRenderer: (p: ICellRendererParams<GridRow>) => {
        if (!p.data) return '';
        const sku = p.data.sku ? escapeHtml(p.data.sku) : '—';
        // Reine Anzeige, KEIN Link (bewusst ohne skulink → kein Link-Hover) - die Gambio-Deeplinks sitzen in der Aktionen-Spalte.
        const label = `<span>${sku}</span>`;
        // Variantenhauptartikel: fett + grünes Verzweigungs-Icon (Tooltip mit Variantenzahl).
        if (p.data.isVariantMain) {
          const n = p.data.variantCount ?? 0;
          return `<span class="sku-cell parent"><span class="arttype" title="Variantenhauptartikel (bestandslos) - ${n} ${n === 1 ? 'Variante' : 'Varianten'}">${SVG_ART_MAIN}</span>${label}</span>`;
        }
        // Variante: eingerückt + graues Paket-Icon (Tooltip mit Variantenfamilie).
        if (p.data.isVariant) {
          const fam = p.data.parentModel ? ` von ${escapeHtml(p.data.parentModel)}` : '';
          return `<span class="sku-cell child"><span class="arttype" title="Artikel mit Bestand · Variante${fam}">${SVG_ART_VARIANT}</span>${label}${familienHinweis(p.data)}</span>`;
        }
        // Flacher Artikel: graues Paket-Icon, nicht eingerückt.
        return `<span class="sku-cell"><span class="arttype" title="Artikel mit Bestand">${SVG_ART_VARIANT}</span>${label}</span>`;
      },
    },
    {
      headerName: 'EAN', field: 'ean', width: 138,
      editable: (p) => !!p.data?.varioArticleId,   // Doppelklick (ag-grid-Default: singleClickEdit=false)
      valueSetter: makeEanSetter(),
      cellRenderer: (p: ICellRendererParams<GridRow>) => (p.value ? `<span class="c-mono">${escapeHtml(String(p.value))}</span>` : '<span class="c-dash">—</span>'),
    },
    {
      headerName: 'Titel', field: 'title', flex: 1, minWidth: 170,
      cellRenderer: (p: ICellRendererParams<GridRow>) => {
        const t = p.value ? escapeHtml(String(p.value)) : '—';
        return `<span class="c-title" title="${t}">${t}</span>`;
      },
    },
    {
      headerName: 'Preis (brutto)', field: 'price', width: 108, cellClass: 'r', headerClass: 'hr',
      headerTooltip: 'Verkaufspreis inkl. MwSt. (aus Netto × Steuersatz der Steuerklasse). Orange = neuer Preis, Übertragung an Gambio steht noch aus.',
      // Orange, solange der zuletzt gesetzte Ziel-Preis noch nicht an Gambio übertragen ist.
      cellClassRules: { 'gm-cell-will-change': (p) => !!p.data && pricePushPending(p.data) },
      cellRenderer: (p: ICellRendererParams<GridRow>) => {
        const d = p.data;
        if (!d) return '';
        const rates = taxRatesByShop.get(d.storefront) ?? {};
        // Push steht aus → sofort den ZIEL-Netto (channel_price_cents) als Brutto zeigen, damit
        // die Liste den neuen Preis führt, bevor Gambio ihn übernommen hat.
        const pending = pricePushPending(d);
        const baseNet = pending && d.channelPriceCents != null ? d.channelPriceCents / 100 : d.price;
        const gross = grossForRow(baseNet, d.taxClassId, rates);
        const txt = fmtPrice(gross);
        // Varianten: Klick öffnet das Variantenpreis-Modal (Auf-/Abschlag + Endpreis).
        if (d.isVariant && d.varioArticleId) {
          return `<a class="c-eur skulink gm-price-edit" data-act="edit-variant-price" data-item="${escapeHtml(d.idItem)}" data-sf="${escapeHtml(d.storefront)}" title="Variantenpreis bearbeiten">${txt}</a>`;
        }
        // Nur Nicht-Varianten mit Vario-Zuordnung sind editierbar (Kundengruppen-/Staffelpreise
        // hängen am Produkt, nicht am Varianten-Kind). Sonst wie bisher nur Anzeige.
        if (!d.isVariant && d.varioArticleId) {
          const title = pending ? 'Neuer Preis, Übertragung an Gambio steht aus' : 'Kundengruppen-/Staffelpreise bearbeiten';
          return `<a class="c-eur skulink gm-price-edit" data-act="edit-price" data-item="${escapeHtml(d.idItem)}" data-sf="${escapeHtml(d.storefront)}" title="${title}">${txt}</a>`;
        }
        return `<span class="c-eur">${txt}</span>`;
      },
    },
    {
      headerName: 'Gambio-Status',
      field: 'isActive',
      width: 130,
      headerTooltip: 'Ob der Artikel im Gambio-Shop aktiv/sichtbar ist (Gambio-Feld „isActive") - Klick schaltet um. Unabhängig von der Vario-Zuordnung.',
      cellRenderer: (p: ICellRendererParams<GridRow>) => {
        // Gambio-Shop-Aktivierung (isActive), NICHT der Vario-/Zuordnungs-Status.
        // Varianten (Gambio v3) tragen kein isActive-Flag → null = „—" (kein Schalter).
        if (!p.data || p.data.isActive === null) {
          return `<span class="c-dash" title="Varianten tragen in Gambio kein Aktiv-Flag">—</span>`;
        }
        const on = p.data.isActive === true;
        const busy = togglingActive.has(`${p.data.storefront}::${p.data.idItem}`);
        return `<button type="button" class="sw-toggle${on ? ' on' : ''}" data-act="toggle-active" data-sf="${escapeHtml(p.data.storefront)}" data-id="${escapeHtml(p.data.idItem)}" title="${on ? 'Im Gambio-Shop deaktivieren' : 'Im Gambio-Shop aktivieren'}"${busy ? ' disabled' : ''}><span class="track"></span><span class="lbl">${on ? 'Aktiv' : 'Inaktiv'}</span></button>`;
      },
    },
    {
      headerName: 'G-Bestand', colId: 'quantity', width: 92, cellClass: 'r', headerClass: 'hr',
      // Nicht sortierbar: der angezeigte Wert ist ABGELEITET (bestandslose Artikel zeigen Max bzw.
      // den virtuellen Lagerbestand), die Datenbank kennt nur die rohe Menge. Serverseitig danach
      // zu sortieren wuerde eine andere Reihenfolge liefern, als hier zu lesen ist.
      sortable: false,
      // Abgeleitet: bestandslose Artikel zeigen den gehaltenen Wert (max ?? virtueller Bestand)
      // → sofortige Reaktion auf Min/Max-Änderungen ohne Cache-/Worker-Lag.
      valueGetter: (p) => (p.data ? effectiveStock(p.data) : null),
      headerTooltip: 'Gambio-Bestand. Bestandslose Artikel: gehaltener Wert (Max bzw. virtueller Lagerbestand). Orange = nächster Abgleich ändert den Gambio-Bestand.',
      // Orange, wenn der nächste Abgleich den Gambio-Bestand ändern würde (Spiegel des push_stock-Workers).
      cellClassRules: {
        'gm-cell-will-change': (p) =>
          !!p.data && stockPushPending(p.data, virtualStockByShop.get(p.data.storefront) ?? DEFAULT_VIRTUAL_STOCK, syncDirectionByShop.get(p.data.storefront) ?? DEFAULT_SYNC_DIRECTION),
      },
      tooltipValueGetter: (p) =>
        p.data && stockPushPending(p.data, virtualStockByShop.get(p.data.storefront) ?? DEFAULT_VIRTUAL_STOCK, syncDirectionByShop.get(p.data.storefront) ?? DEFAULT_SYNC_DIRECTION)
          ? 'Bestand wird beim nächsten Abgleich an Gambio angepasst.'
          : undefined,
      cellRenderer: (p: ICellRendererParams<GridRow>) =>
        p.value == null ? '<span class="c-dash">—</span>' : `<span class="c-num ${p.value === 0 ? 'zero' : ''}">${p.value}</span>`,
    },
    {
      headerName: 'V-Bestand', field: 'varioStock', width: 104, cellClass: 'r', headerClass: 'hr', editable: false,
      cellRenderer: (p: ICellRendererParams<GridRow>) => (p.value == null ? '<span class="c-dash">—</span>' : `<span class="c-num">${p.value}</span>`),
    },
    {
      headerName: 'Min',
      field: 'minBestand',
      width: 84,
      cellClass: 'r',
      headerClass: 'hr',
      // Min/Max für gemappte Artikel editierbar - auch Varianten (Varianten-Bestand wird via v3
      // PATCH /products/{id}/variants gepusht). Hauptartikel ausgenommen (nicht bestandsgeführt).
      editable: editableMinMax,
      cellClassRules: { edit: editableMinMax },
      cellEditor: 'agNumberCellEditor',
      cellRenderer: (p: ICellRendererParams<GridRow>) =>
        p.value == null ? (editableMinMax(p) ? '<span class="c-edit-empty">+</span>' : '<span class="c-dash">—</span>') : `<span class="c-num">${p.value}</span>`,
      valueSetter: makeMinMaxSetter('min'),
    },
    {
      headerName: 'Max',
      field: 'maxBestand',
      width: 84,
      cellClass: 'r',
      headerClass: 'hr',
      editable: editableMinMax,
      cellClassRules: { edit: editableMinMax },
      cellEditor: 'agNumberCellEditor',
      cellRenderer: (p: ICellRendererParams<GridRow>) =>
        p.value == null ? (editableMinMax(p) ? '<span class="c-edit-empty">+</span>' : '<span class="c-dash">—</span>') : `<span class="c-num">${p.value}</span>`,
      valueSetter: makeMinMaxSetter('max'),
    },
    {
      headerName: 'Aktionen',
      field: 'varioArticleId',
      width: 166,
      sortable: false,
      resizable: false,
      pinned: 'right',
      cellRenderer: (p: ICellRendererParams<GridRow>) => {
        if (!p.data) return '';
        const sf = escapeHtml(p.data.storefront);
        const id = escapeHtml(p.data.idItem);
        const base = shopBaseByKey.get(p.data.storefront);
        const pid = base ? gambioProductId(p.data.idItem) : null;
        const admin = base && pid ? gambioProductAdminUrl(base, pid) : null;
        const front = base && pid ? gambioProductFrontendUrl(base, pid) : null;
        // Gambio-Deeplinks (iframe-sicher als <a target="_blank">, kein window.open).
        const adminA = admin ? `<a class="act-icon" href="${escapeHtml(admin)}" target="_blank" rel="noopener" title="In Gambio Backend bearbeiten">${SVG_PEN}</a>` : '';
        // Frontend-Link bei nachweislich inaktivem Artikel sperren — das Shop-Frontend zeigt inaktive
        // Artikel nicht, der Link liefe ins Leere. Der Link zeigt bei allen Varianten auf die
        // ELTERNprodukt-Seite, daher richtet sich der Aktiv-Status am Elternprodukt aus (nicht am Kind,
        // das kein eigenes Flag trägt). Bei aktiv oder unbekannt bleibt der Link klickbar.
        // Wirksamer Status der Zeile (bei Varianten der des Elternprodukts) - kommt aus der Sicht,
        // frueher eine Karte ueber ALLE Zeilen.
        const parentInactive = p.data.effectiveIsActive === false;
        const frontA = front
          ? (parentInactive
              ? `<span class="act-icon" aria-disabled="true" title="Artikel ist im Gambio-Shop inaktiv und daher im Frontend nicht aufrufbar" style="opacity:.4;cursor:not-allowed;pointer-events:none;">${SVG_EXTERNAL}</span>`
              : `<a class="act-icon" href="${escapeHtml(front)}" target="_blank" rel="noopener" title="In Gambio Frontend öffnen">${SVG_EXTERNAL}</a>`)
          : '';

        // --- GEMAPPT: Herunterladen (G→V) + Auflösen (zweistufig) ---
        if (p.data.varioArticleNumber) {
          const key = escapeHtml(unassignRowKey(p.data));
          const tail = confirmingUnassign.has(unassignRowKey(p.data))
            ? `<button type="button" class="unbind" data-act="unassign-confirm" data-key="${key}" data-sf="${sf}" data-id="${id}" title="Aus dem Gambio-Kanal nehmen (das Listing wird inaktiv, die Verknüpfung bleibt)">${SVG_UNBIND} Wirklich?</button>
               <button type="button" class="act-icon" data-act="unassign-cancel" data-key="${key}" title="Abbrechen">✕</button>`
            : `<button type="button" class="act-icon" data-act="upload-to-gambio" data-sf="${sf}" data-id="${id}" data-vaid="${escapeHtml(String(p.data.varioArticleId))}" title="Hochladen - Vario-Artikel in Gambio aktualisieren">${SVG_UPLOAD}</button>
               <button type="button" class="act-icon" data-act="refresh-from-gambio" data-sf="${sf}" data-id="${id}" title="Herunterladen - Gambio-Artikel nach Vario aktualisieren">${SVG_DOWNLOAD}</button>
               <button type="button" class="act-icon danger" data-act="unassign" data-key="${key}" data-sf="${sf}" data-id="${id}" title="Aus dem Gambio-Kanal nehmen (Listing wird inaktiv, die Verknüpfung bleibt)">${SVG_UNBIND}</button>`;
          return `<span class="act-cell">${adminA}${frontA}${tail}</span>`;
        }
        // --- NICHT GEMAPPT: Suggestion vorhanden → Verknüpfen ---
        if (p.data.suggestedVarioId) {
          const srcLabel = p.data.matchSource === 'ean' ? 'EAN' : 'SKU';
          const hint = escapeHtml(`Verknüpfen mit ${p.data.suggestedVarioNumber ?? ''} (Treffer via ${srcLabel})`);
          return `<span class="act-cell">${adminA}${frontA}<button type="button" class="act-icon" data-act="assign-row" data-sf="${sf}" data-id="${id}" title="${hint}">${SVG_LINK}</button></span>`;
        }
        // --- Variantenhauptartikel: Container ohne eigenen Vario-Artikel → keine Zuordnungsaktion
        //     (die Kinder sind einzeln zugeordnet). Nur die Deeplinks. ---
        if (p.data.isVariantMain) {
          return `<span class="act-cell">${adminA}${frontA}</span>`;
        }
        // --- NICHT GEMAPPT, keine Suggestion: Varianten-Kind → nur Zuordnung möglich ---
        if (p.data.isVariant) {
          return `<span class="act-cell">${adminA}${frontA}<span class="art-tag" title="Varianten können nicht flach in VARIO angelegt werden - nur verknüpfen, sobald ein passender VARIO-Artikel existiert.">nur Verknüpfung möglich</span></span>`;
        }
        // --- NICHT GEMAPPT, keine Suggestion, flach: Anlegen ---
        // Inaktive Artikel werden NICHT uebernommen. Betreiber-Vorgabe 25.09.2026, nachdem
        // `50-126` („Spiegelei-Pfanne", im Shop inaktiv) und `22927` in eine Anlage-Welle geraten
        // waren: in VARIO soll nur landen, was im Shop auch verkauft wird.
        if (parentInactive) {
          return `<span class="act-cell">${adminA}${frontA}<span class="act-icon" aria-disabled="true" title="Artikel ist im Gambio-Shop inaktiv - es werden nur aktive Artikel in VARIO angelegt" style="opacity:.4;cursor:not-allowed;pointer-events:none;">${SVG_PLUS}</span></span>`;
        }
        return `<span class="act-cell">${adminA}${frontA}<button type="button" class="act-icon" data-act="seed-row" data-sf="${sf}" data-id="${id}" title="In VARIO anlegen">${SVG_PLUS}</button></span>`;
      },
    },
  ];

  // Filter-Zustand der Filterleiste. Wird von den Controls befüllt und in `buildQuery` bzw. den
  // Filter-RPCs serverseitig ausgewertet — nicht mehr im Browser über allen Zeilen.
  // mapping 'in-vario' = nicht zugeordnet, aber ein Vario-Match (Suggestion) vorhanden.
  const filters: { search: string; status: '' | 'active' | 'inactive'; mapping: '' | 'mapped' | 'unmapped' | 'in-vario' } = {
    search: '',
    status: '',
    mapping: '',
  };
  // Suchfilter über Tab-Refresh / F5 hinweg erhalten (sessionStorage).
  const FILTERS_KEY = 'gambio:products:filters';
  try {
    const raw = sessionStorage.getItem(FILTERS_KEY);
    if (raw) {
      const p = JSON.parse(raw) as Partial<typeof filters>;
      if (typeof p.search === 'string') filters.search = p.search;
      if (p.status === 'active' || p.status === 'inactive') filters.status = p.status;
      if (p.mapping === 'mapped' || p.mapping === 'unmapped' || p.mapping === 'in-vario') filters.mapping = p.mapping;
    }
  } catch { /* ignore */ }
  const persistFilters = () => {
    try { sessionStorage.setItem(FILTERS_KEY, JSON.stringify(filters)); } catch { /* ignore */ }
  };

  /**
   * Suchbegriff entschaerfen. `%`, `*`, `(`, `)`, `,`, `"` und `\` zerlegen entweder das
   * ilike-Muster oder die `or=(…)`-Syntax von PostgREST. Derselbe bereinigte Wert geht auch an die
   * serverseitigen Filter-RPCs, damit Anzeige und Sammelaktion dieselbe Treffermenge meinen.
   */
  const sanitizedSearch = (): string => filters.search.replace(/[%*,()\\"]/g, ' ').trim();
  /** Wirksamer Filterzustand als Zeichenkette - Vergleichswert gegen ueberfluessige Neuladungen. */
  const filterZustand = (): string => [sanitizedSearch(), filters.status, filters.mapping].join('\u0001');
  /** Der Mapping-Filter heisst serverseitig `in_vario`, in der Oberflaeche `in-vario`. */
  const mappingForRpc = (): string | null => (filters.mapping ? filters.mapping.replace('-', '_') : null);

  /** Trefferzahl des aktuellen Filters, vom zuletzt geladenen Block gemeldet. */
  let totalRows = 0;
  /** Zuletzt an den Server geschickter Filterzustand - gegen ueberfluessige Neuladungen. */
  let letzterFilter = '';
  /**
   * „Alle Treffer" statt der angehakten Zeilen. In diesem Zustand schicken die Sammelaktionen den
   * Filter an den Server (`gambio_enqueue_bulk_by_filter`), nicht die Kennungen der geladenen
   * Zeilen — sonst wuerde eine Aktion auf 150.000 Treffer stillschweigend nur 100 Artikel treffen.
   */
  let selectAllFiltered = false;
  /** Shop-Schluessel aus `gambio_list_shops`; die Filter-RPCs arbeiten je Shop. */
  const shopKeys: string[] = [];

  const options: GridOptions<GridRow> = {
    columnDefs: cols,
    // Infinite Row Model: die Tabelle holt nur den sichtbaren Block, nicht den ganzen Bestand.
    // Community-Edition reicht dafuer (das Server-Side Row Model waere Enterprise).
    rowModelType: 'infinite',
    // Blockgroesse = Seitengroesse. Beim Blaettern muessen beide zusammenpassen, sonst laedt eine
    // Seite aus zwei Bloecken. Betreiber-Entscheidung 27.08.2026: feste 100 statt waehlbarer
    // 50/100/200 - eine zur Laufzeit aenderbare Blockgroesse gibt AG Grid nicht her.
    cacheBlockSize: 100,
    // Hoechstens 10 Bloecke im Speicher; aeltere werden verworfen und bei Bedarf neu geholt.
    maxBlocksInCache: 10,
    infiniteInitialRowCount: 1,
    defaultColDef: { sortable: true, resizable: true, suppressHeaderMenuButton: true },
    // Neue v32-Selection-API (wie orders.ts): Checkboxen über rowSelection.
    // KEIN Kopfzeilen-Haken: im Infinite Row Model kennt die Tabelle nur die geladenen Bloecke.
    // Ein Haken, der „alles auswaehlen" verspricht, wuerde bei 150.000 Artikeln stillschweigend
    // die 100 Zeilen der aktuellen Seite meinen. Stattdessen bietet die Bulk-Leiste ausdruecklich
    // „Alle N Treffer auswaehlen" an, und die Sammelaktionen schicken dann den FILTER an den
    // Server statt einer ID-Liste (Migration 0345).
    rowSelection: { mode: 'multiRow', enableClickSelection: false, checkboxes: true, headerCheckbox: false },
    // Stabile Zeilen-Id: `storefront::idItem`. Gambio-Produkt-IDs sind nur je Shop eindeutig.
    // Damit laesst sich eine Zeile direkt nachschlagen, statt sie zu suchen, und die Auswahl
    // ueberlebt spaeter das Nachladen von Bloecken.
    getRowId: (p) => `${p.data.storefront}::${p.data.idItem}`,
    selectionColumnDef: { width: 46, pinned: 'left', resizable: false },
    rowHeight: 38,
    headerHeight: 38,
    pagination: true,
    paginationPageSize: 100,
    paginationPageSizeSelector: false,
    stopEditingWhenCellsLoseFocus: true,
    overlayNoRowsTemplate: 'Noch keine Produkte synchronisiert. Klick „Produkte aktualisieren".',
    rowClassRules: {
      // Hellblau (row-child) = Varianten-KIND (wie im Design-Mock). Varianten-Kinder werden so
      // zusätzlich zur SKU-Zelle (Icon + Einrückung) farblich von Haupt-/Flachartikeln abgesetzt.
      'row-child': (p) => p.data?.isVariant === true,
    },
    onSelectionChanged: (e) => {
      // Ein Klick auf eine Zeilen-Checkbox beendet den „alle Treffer"-Modus. Sonst behauptet die
      // Leiste weiter „N ausgewählt", während die Haken in den Zeilen etwas anderes zeigen.
      // Programmatische Änderungen (`api…`-Quellen, z. B. `deselectAll`) sind ausgenommen.
      if (selectAllFiltered && !e.source.startsWith('api')) selectAllFiltered = false;
      updateBulk();
    },
    // ag-Grid v32: Custom-cellRenderer (DOM) werden von redrawRows geleert → nach Render
    // refreshCells({force}) erzwingen (siehe Handoff-README).
    onFirstDataRendered: (e) => e.api.refreshCells({ force: true }),
    onGridSizeChanged: (e) => e.api.refreshCells({ force: true }),
  };
  const api: GridApi<GridRow> = createGrid(gridHost, options);
  // ag-Grid v32: nach Init Zellen einmal forciert neu zeichnen (siehe README-Hinweis).
  setTimeout(() => api.refreshCells({ force: true }), 120);

  /**
   * Hakt alle GELADENEN Zeilen ab - die sichtbare Rueckmeldung zum "alle Treffer"-Zustand.
   *
   * NICHT `api.selectAll()`: das ruft intern `validateSelectAllType()` und WIRFT im Infinite Row
   * Model ("selectAll only available when rowModelType='clientSide'"). Genau daran ist der erste
   * Versuch gescheitert - der Klick blieb folgenlos, weil der Ereignisbehandler an der Ausnahme
   * starb (Betreiber 27.08.2026). `setNodesSelected` hat diese Sperre nicht; es verlangt nur eine
   * bekannte Zeilen-Id, und die liefert `getRowId`. Im Quelltext von ag-grid-community@32.3.9
   * nachgesehen, nicht angenommen.
   */
  function hakeGeladeneZeilenAb(): void {
    const nodes: IRowNode<GridRow>[] = [];
    api.forEachNode((n) => { if (n.data && !n.isSelected()) nodes.push(n); });
    if (nodes.length > 0) api.setNodesSelected({ nodes, newValue: true, source: 'api' });
  }

  function updateBulk(): void {
    const n = api.getSelectedNodes().length;
    // Der Knopf sitzt in der FILTERLEISTE, nicht in der Auswahlleiste: die ist erst sichtbar,
    // wenn schon etwas markiert ist, und dann waere "alle Treffer uebernehmen" nie ein Klick
    // (Betreiber 27.08.2026). Jetzt steht er neben der Trefferzahl und ist immer erreichbar.
    selAllBtn.hidden = selectAllFiltered || totalRows === 0;
    selAllBtn.textContent = `Alle ${totalRows} auswählen`;
    selAllNote.hidden = !selectAllFiltered;
    // Im "alle Treffer"-Zustand zaehlt nicht die Zahl der Haken, sondern die Trefferzahl.
    selCnt.textContent = String(selectAllFiltered ? totalRows : n);
    bulkbar.classList.toggle('show', selectAllFiltered || n > 0);
    // Der Preis-Import laeuft ueber eine Edge Function und kennt keinen Filter-Weg.
    importPricesBtn.disabled = selectAllFiltered;
    importPricesBtn.title = selectAllFiltered
      ? 'Der Preis-Import läuft über die einzelnen Artikel — dafür bitte Zeilen markieren statt alle Treffer zu wählen.'
      : 'Kundengruppen-/Staffelpreise der markierten zugeordneten Artikel aus Gambio nach Vario importieren';
  }

  /**
   * Reiht eine Sammelaktion ueber ALLE Treffer des aktuellen Filters ein — serverseitig.
   * `gambio_enqueue_bulk_by_filter` (Migration 0345) loest den Filter in der Datenbank auf und
   * ruft damit dieselben Enqueue-RPCs auf wie der Weg ueber die markierten Zeilen. Hier wird
   * bewusst keine Geschaeftslogik nachgebaut.
   */
  async function enqueueByFilter(
    action: 'assign' | 'seed' | 'download' | 'create_variants' | 'min_max',
    extra: Record<string, unknown> = {},
  ): Promise<string[]> {
    const jobIds: string[] = [];
    // „Alle Treffer" + Anlegen: der Status-Filter wird auf `active` gezwungen. Ueber alle Treffer
    // laesst sich nicht zeilenweise aussortieren wie bei der Auswahl, also uebernimmt das die
    // Abfrage in der Datenbank (`effective_is_active is true`, Migration 0364). Inaktive Artikel
    // werden nicht in VARIO angelegt - Betreiber-Vorgabe 25.09.2026. Der Aufrufer sagt das dem
    // Anwender, damit die Trefferzahl im Ergebnis nicht unerklaert kleiner ist.
    const status = action === 'seed' ? 'active' : (filters.status || null);
    for (const shopKey of shopKeys) {
      const { data, error } = await ctx.supabase.rpc('gambio_enqueue_bulk_by_filter', {
        _shop_key: shopKey,
        _action: action,
        _search: sanitizedSearch() || null,
        _status: status,
        _mapping: mappingForRpc(),
        ...extra,
      });
      if (error) throw new Error(error.message);
      for (const rec of (Array.isArray(data) ? data : [data])) {
        const id = typeof rec === 'string' ? rec : (rec as { job_id?: string } | null)?.job_id;
        if (id) jobIds.push(id);
      }
    }
    return jobIds;
  }

  /**
   * Steht die Liste gerade in Familien-Reihenfolge (Kind direkt unter seinem Hauptartikel)? Nur
   * dann darf die Einrueckung fuer sich sprechen. Sortiert der Benutzer nach Titel, Preis oder
   * Bestand, reisst die Familie auseinander, und ein eingeruecktes Kind saehe aus, als gehoere es
   * zur Zeile darueber. In dem Fall nennt die Zelle die Familie im Klartext.
   */
  let familienSortierung = true;

  const SELECT_COLS = 'id_item, storefront, sku, ean, title, price, quantity, is_active,'
    + ' effective_is_active, effective_tax_class_id, is_variant, is_variant_main, variant_count,'
    + ' parent_model, vario_article_id, vario_article_number, vario_stock, channel_price_cents,'
    + ' min_bestand, max_bestand, vario_product_type, vario_article_missing_since,'
    + ' suggested_vario_article_id,'
    + ' suggested_vario_article_number, suggested_match_source, listing_id, vario_listing_id,'
    + ' vario_main_article_id, parent_vario_article_id, parent_listing_id, is_mapped';

  /** Sortierbare Grid-Spalte -> Spalte der Sicht. Unbekanntes faellt auf die Standardsortierung. */
  const SORT_COLS: Record<string, string> = {
    varioArticleNumber: 'vario_article_number', sku: 'sku', ean: 'ean', title: 'title',
    price: 'price', varioStock: 'vario_stock',
    minBestand: 'min_bestand', maxBestand: 'max_bestand', isActive: 'is_active',
  };

  /** Eine Zeile der Sicht in die Grid-Zeile uebersetzen. */
  function mapRow(it: OverviewRow): GridRow {
    return {
      idItem: it.id_item,
      storefront: it.storefront,
      sku: it.sku,
      ean: it.ean,
      title: it.title,
      price: it.price == null ? null : Number(it.price),
      quantity: it.quantity == null ? null : Number(it.quantity),
      // EIGENER Wert - die Spalte „Gambio-Status" zeigt ihn an und muss bei einer Variante „—"
      // bleiben. Gefiltert wird ueber `effective_is_active`, siehe `buildQuery`.
      isActive: it.is_active,
      effectiveIsActive: it.effective_is_active,
      isMapped: it.is_mapped,
      taxClassId: it.effective_tax_class_id,
      isVariant: it.is_variant,
      isVariantMain: it.is_variant_main,
      variantCount: it.variant_count,
      parentModel: it.parent_model,
      varioArticleNumber: it.vario_article_number ?? (it.vario_article_id ? '—' : null),
      varioArticleMissingSince: it.vario_article_missing_since,
      varioArticleId: it.vario_article_id,
      varioMainArticleId: it.is_variant_main ? it.vario_main_article_id : null,
      varioListingId: it.vario_listing_id,
      gambioListingId: it.listing_id,
      varioStock: it.vario_stock,
      channelPriceCents: it.channel_price_cents,
      minBestand: it.min_bestand,
      maxBestand: it.max_bestand,
      varioProductType: it.vario_product_type,
      suggestedVarioId: it.suggested_vario_article_id,
      suggestedVarioNumber: it.suggested_vario_article_number,
      matchSource: it.suggested_match_source,
      parentVarioArticleId: it.parent_vario_article_id,
      parentListingId: it.parent_listing_id,
    };
  }

  /**
   * Baut die Abfrage fuer einen Block. Suche, Filter und Sortierung laufen damit in der Datenbank.
   * Vorher lief beides im Browser ueber dem vollstaendig geladenen Bestand — das traegt bei
   * 150.000 Artikeln (Betreiber-Vorgabe 27.08.2026) nicht mehr.
   */
  function buildQuery(
    from: number,
    to: number,
    sortModel: Array<{ colId: string; sort: string }>,
    withCount: boolean,
  ) {
    let q = ctx.supabase.schema('gambio').from('products_overview_ui')
      .select(SELECT_COLS, withCount ? { count: 'exact' } : undefined);
    const term = sanitizedSearch();
    if (term) q = q.or(`sku.ilike.%${term}%,ean.ilike.%${term}%,title.ilike.%${term}%`);
    // Welche Spalten die beiden Auswahlfelder treffen, steht in `products-filter.ts` - dort auch
    // die Begruendung. Kurz: Status gegen `effective_is_active`, weil Varianten kein eigenes
    // `isActive` tragen, und Zuordnung gegen die `group_has_*`-Spalten, weil die Variantengruppe
    // als Einheit erscheinen soll (Betreiber-Entscheidung 20.09.2026, Migration 0364). Bewusst in
    // Kauf genommen: „Nicht zugeordnet" listet dann auch schon zugeordnete Geschwister mit, und
    // die Trefferzahl zaehlt gezeigte Zeilen, nicht offene Posten.
    for (const c of productFilterConditions(filters.status, filters.mapping)) q = q.is(c.column, c.value);
    const sort = sortModel.find((sm) => SORT_COLS[sm.colId]);
    // Die Liste ist flach, Varianten-Kinder sind nur eingerueckt und blau. Sortiert man stumpf nach
    // der SKU, landet ein Kind dort, wo sein Name alphabetisch hinfaellt, und nicht bei seinem
    // Vater: 120-I und 120-N standen so unter dem Block von 120-EVO, waehrend 120 kinderlos aussah
    // (Betreiber-Meldung 23.09.2026). Bei den beiden Nummern-Spalten sortieren wir deshalb ueber die
    // Familie (`family_sku`/`family_rank` aus Migration 0365), damit jedes Kind unter seinem eigenen
    // Hauptartikel steht.
    familienSortierung = !sort || sort.colId === 'sku' || sort.colId === 'varioArticleNumber';
    if (familienSortierung) {
      const aufsteigend = sort ? sort.sort === 'asc' : true;
      // `family_rank` bleibt aufsteigend, auch wenn der Benutzer absteigend sortiert: der
      // Hauptartikel gehoert ueber seine Kinder, sonst steht die Familie auf dem Kopf.
      q = q.order('family_sku', { ascending: aufsteigend, nullsFirst: false })
        .order('storefront', { ascending: true })
        .order('family_rank', { ascending: true })
        .order('sku', { ascending: aufsteigend, nullsFirst: false });
    } else {
      q = q.order(SORT_COLS[sort!.colId]!, { ascending: sort!.sort === 'asc', nullsFirst: false })
        .order('storefront', { ascending: true });
    }
    // Stabile Letztsortierung. Ohne sie kann dieselbe Zeile in zwei Bloecken auftauchen oder in
    // keinem, sobald das Sortierfeld nicht eindeutig ist (SKU ist es nicht ueber mehrere Shops).
    q = q.order('id_item', { ascending: true });
    return q.range(from, to);
  }

  /** Shop-abhaengige Stammdaten: Basis-URLs, virtueller Bestand, Sync-Richtung, Steuersaetze. */
  async function loadShopMeta(): Promise<void> {
    const shopsRes = await ctx.supabase.rpc('gambio_list_shops');
    shopBaseByKey.clear();
    shopKeys.length = 0;
    for (const sh of (Array.isArray(shopsRes.data) ? shopsRes.data : []) as Array<{ shop_key: string; base_url: string | null }>) {
      shopKeys.push(sh.shop_key);
      if (sh.base_url) shopBaseByKey.set(sh.shop_key, sh.base_url);
    }
    // Virtuellen Lagerbestand + sync_direction je Shop laden (für die abgeleitete „Bestand"-Anzeige
    // bestandsloser Artikel bzw. das Richtungs-Gate der Push-Markierung). Beides kommt aus derselben
    // RPC. Best-effort pro Shop; bei Fehler Defaults. Wenige Gambio-Shops → wenige Calls.
    virtualStockByShop.clear();
    syncDirectionByShop.clear();
    await Promise.all(shopKeys.map(async (sk) => {
      try {
        const { data } = await ctx.supabase.rpc('gambio_get_shop_settings', { _shop_key: sk });
        const row = (Array.isArray(data) ? data[0] : data) as { virtual_stock?: number | null; sync_direction?: string | null } | undefined;
        const vs = row?.virtual_stock;
        virtualStockByShop.set(sk, typeof vs === 'number' && Number.isFinite(vs) ? vs : DEFAULT_VIRTUAL_STOCK);
        syncDirectionByShop.set(sk, typeof row?.sync_direction === 'string' && row.sync_direction ? row.sync_direction : DEFAULT_SYNC_DIRECTION);
      } catch {
        virtualStockByShop.set(sk, DEFAULT_VIRTUAL_STOCK);
        syncDirectionByShop.set(sk, DEFAULT_SYNC_DIRECTION);
      }
    }));
    // Steuersätze je Shop einmal pro Session laden (für die Brutto-Anzeige). Steuerklassen
    // wechseln praktisch nie → bereits geladene Shops überspringen. Best-effort; leer bei Fehler.
    await Promise.all(shopKeys.filter((sk) => !taxRatesByShop.has(sk)).map(async (sk) => {
      try {
        const { data } = await callProductPrices(ctx, { action: 'tax-rates', storefront: sk });
        const rates = (data as { ratesByClassId?: Record<string, number> } | null)?.ratesByClassId ?? {};
        taxRatesByShop.set(sk, rates);
      } catch { taxRatesByShop.set(sk, {}); }
    }));
  }

  /**
   * Setzt die Datenquelle neu. Verwirft den Blockspeicher und ermittelt die Trefferzahl neu —
   * das ist der Weg fuer einen FILTERWECHSEL. Fuer „gleicher Filter, Daten haben sich geaendert"
   * ist `api.refreshInfiniteCache()` richtig: das haelt die Seitenposition.
   */
  function setDatasource(): void {
    // Hier - und nur hier - geht der Filter an den Server. Deshalb wird der Vergleichswert genau
    // hier fortgeschrieben, auch beim ersten Laden mit einem aus der Sitzung wiederhergestellten
    // Filter; sonst haette `applyFilters` beim ersten Wechsel einen falschen Ausgangswert.
    letzterFilter = filterZustand();
    // Leer-Meldung an den Filterzustand koppeln - „Noch keine Produkte synchronisiert“ waere bei
    // einer Suche ohne Treffer schlicht falsch.
    const gefiltert = !!(sanitizedSearch() || filters.status || filters.mapping);
    api.setGridOption('overlayNoRowsTemplate', gefiltert
      ? 'Keine Produkte passen zu Suche und Filter.'
      : 'Noch keine Produkte synchronisiert. Klick „Produkte aktualisieren“.');
    api.setGridOption('datasource', {
      getRows: (params) => {
        void (async () => {
          try {
            // `count: 'exact'` NUR beim ersten Block. Der Count kostet einen eigenen Durchlauf
            // ueber die gefilterte Menge (gemessen 27.08.2026: 6,5 ms bei 1.783 Artikeln,
            // hochgerechnet Hunderte Millisekunden bei 150.000) — bei jedem der bis zu zehn
            // Bloecke waere das Verschwendung. Block 0 laedt immer zuerst: ohne bekannte
            // Zeilenzahl kann die Blaetterleiste gar nicht auf eine spaetere Seite springen.
            const withCount = params.startRow === 0;
            const res = await buildQuery(
              params.startRow,
              params.endRow - 1,
              params.sortModel as Array<{ colId: string; sort: string }>,
              withCount,
            );
            if (res.error) throw new Error(res.error.message);
            const rows = ((res.data ?? []) as unknown as OverviewRow[]).map(mapRow);
            if (withCount) {
              totalRows = res.count ?? rows.length;
            } else if (params.startRow + rows.length > totalRows) {
              // Liefert ein spaeterer Block mehr Zeilen, als die gemerkte Zahl zulaesst, ist die
              // Zahl veraltet — dann nach oben korrigieren statt Zeilen zu verstecken.
              totalRows = params.startRow + rows.length;
            }
            countEl.textContent = `${totalRows} Produkte`;
            params.successCallback(rows, totalRows);
          } catch (ex) {
            params.failCallback();
            openErrorModal({
              title: 'Produkte nicht ladbar',
              items: [{ message: (ex as Error).message }],
              context: { action: 'gambio_load_products' },
              json: ex,
            });
          } finally {
            refreshIcon.classList.remove('fa-spin');
          }
          // BEWUSST hinter dem try/catch: das hier ist Darstellung, nicht Datenbeschaffung. Stand
          // es mit im try, machte ein Fehler beim Nachhaken aus einem erfolgreich geladenen Block
          // einen gescheiterten (`failCallback` nach `successCallback`) und warf dem Anwender ein
          // "Produkte nicht ladbar" hin, obwohl die Daten da waren. Genau so ist es passiert.
          try {
            // Im "alle Treffer"-Zustand die frisch geladenen Zeilen mit abhaken - sonst bleiben die
            // Haken beim Blaettern leer, obwohl die Aktion die Zeilen sehr wohl erfasst.
            if (selectAllFiltered) hakeGeladeneZeilenAb();
            updateBulk();
            // v32-Redraw-Caveat: Custom-Renderer nach dem Nachladen forciert neu zeichnen.
            api.refreshCells({ force: true });
          } catch (ex) {
            // Nicht als Ladefehler melden - die Zeilen stehen. Nur ins Protokoll.
            console.error('Produktuebersicht: Nachbereitung des Blocks fehlgeschlagen', ex);
          }
        })();
      },
    });
  }

  /** Erst nach dem ersten `setDatasource` darf `refreshInfiniteCache` genutzt werden. */
  let datasourceReady = false;

  async function load(): Promise<void> {
    refreshIcon.classList.add('fa-spin');
    await loadShopMeta();
    // Nach einer Sammelaktion ist die alte „alle Treffer"-Auswahl nicht mehr gemeint.
    selectAllFiltered = false;
    updateBulk();
    if (datasourceReady) {
      // Gleicher Filter, neue Daten: Bloecke neu holen und dabei die Seite behalten.
      api.refreshInfiniteCache();
    } else {
      datasourceReady = true;
      setDatasource();
    }
  }

  /**
   * Filterwechsel: Auswahl fällt, Blockspeicher wird verworfen, es geht auf Seite 1 zurück.
   *
   * Vergleicht vorher den WIRKSAMEN Filterzustand. Ohne den Vergleich löst jedes Ereignis eine
   * Serverabfrage aus, auch wenn sich nichts geändert hat — etwa wenn im Suchfeld nur ein Zeichen
   * getippt wird, das `sanitizedSearch()` ohnehin entfernt.
   */
  function applyFilters(): void {
    if (filterZustand() === letzterFilter) return;
    selectAllFiltered = false;
    api.deselectAll();
    refreshIcon.classList.add('fa-spin');
    setDatasource();
    api.paginationGoToFirstPage();
  }

  /**
   * Stößt nach Zuordnen/Anlegen den Vario-Bestands-Refresh je Shop an und wartet ihn ab,
   * damit frisch gemappte Artikel sofort „Vario-Bestand" + „Artikelart" (vario_product_type)
   * zeigen statt erst beim nächsten 5-Min-Cron. Best-effort: bei Fehler füllt der Cron nach.
   */
  async function refreshVarioStocksFor(pairs: Array<{ shopKey: string; idItem: string }>): Promise<void> {
    for (const [sk, ids] of groupByShop(pairs)) {
      try {
        const { data, error } = await ctx.supabase.rpc('gambio_enqueue_stock_refresh', { _shop_key: sk, _id_items: ids });
        if (error) continue;
        const jobId = typeof data === 'string' ? data : (data as { job_id?: string } | null)?.job_id;
        if (jobId) await awaitJob(ctx.supabase, jobId as string, 180_000);
      } catch { /* Cron füllt nach */ }
    }
  }

  /** Gruppiert {shopKey,idItem}-Paare zu shopKey → idItems[] (deduped, leere raus). */
  function groupByShop(pairs: Array<{ shopKey: string; idItem: string }>): Map<string, string[]> {
    const byShop = new Map<string, string[]>();
    for (const p of pairs) {
      if (!p.shopKey || !p.idItem) continue;
      const arr = byShop.get(p.shopKey) ?? [];
      if (!arr.includes(p.idItem)) arr.push(p.idItem);
      byShop.set(p.shopKey, arr);
    }
    return byShop;
  }

  /**
   * Rechnet den Vario-Match je Shop neu (gambio_enqueue_match_suggestions) und wartet ihn ab.
   * Nach dem Auflösen einer Verknüpfung nötig: der Vario-Artikel existiert weiter, also soll
   * die Zeile „Zuordnen" (Match vorhanden) statt „Anlegen" zeigen.
   */
  async function refreshMatchFor(pairs: Array<{ shopKey: string; idItem: string }>): Promise<void> {
    for (const [sk, ids] of groupByShop(pairs)) {
      try {
        const { data, error } = await ctx.supabase.rpc('gambio_enqueue_match_suggestions', { _shop_key: sk, _id_items: ids });
        if (error) continue;
        const jobId = typeof data === 'string' ? data : (data as { job_id?: string } | null)?.job_id;
        if (jobId) await awaitJob(ctx.supabase, jobId as string, 180_000);
      } catch { /* Abgleich-Button füllt sonst nach */ }
    }
  }

  /**
   * Gemeinsame Kernlogik für beide Refresh-Knöpfe. 'incremental' (Standard-Knopf „Produkte
   * aktualisieren"): pullt via `gambio_enqueue_items_refresh_incremental` nur seit dem
   * letzten Abgleich geänderte Produkte (modifiedSince-Wasserzeichen aus gambio.items,
   * siehe Migration 0308), dadurch deutlich weniger v3-Variantencalls im Worker-Job.
   * 'full' (Knopf „Vollständig abgleichen"): bisheriger Voll-Pull inkl. Tombstoning
   * gelöschter Produkte, für den Fall dass der inkrementelle Abgleich etwas übersieht.
   * Beide Knöpfe werden während des Laufs gemeinsam deaktiviert (kein doppelter Trigger).
   */
  async function runRefresh(mode: 'incremental' | 'full'): Promise<void> {
    const activeIcon = mode === 'full' ? fullRefreshIcon : refreshIcon;
    refreshBtn.disabled = true;
    fullRefreshBtn.disabled = true;
    activeIcon.classList.add('fa-spin');
    try {
      // Pull pro Shop anstoßen. gambio_list_shops liefert die shop_keys.
      const shopsRes = await ctx.supabase.rpc('gambio_list_shops');
      const shops = (Array.isArray(shopsRes.data) ? shopsRes.data : []) as Array<{ shop_key: string }>;
      const itemsRefreshRpc = mode === 'incremental' ? 'gambio_enqueue_items_refresh_incremental' : 'gambio_enqueue_items_refresh';
      const jobIds: string[] = [];
      for (const s of shops) {
        const { data, error } = await ctx.supabase.rpc(itemsRefreshRpc, { _shop_key: s.shop_key });
        if (error) throw new Error(error.message);
        const rec = Array.isArray(data) ? data[0] : data;
        if (rec?.job_id) jobIds.push(rec.job_id as string);
      }
      showToast(mode === 'incremental' ? 'Produkt-Sync läuft …' : 'Vollständiger Abgleich läuft (kann dauern) …', 'info');
      // Ergebnisse ALLER Teilschritte einsammeln (Items-Pull, Kategorien, Treffer, Bestand).
      // Vorher lief das über ein einzelnes `anyFailed`-Flag, dessen Meldung nur auf die
      // Worker-Logs verwies; jetzt nennt das Modal Schritt und Ursache.
      const syncOutcomes: BulkJobOutcome[] = [];
      syncOutcomes.push(...(await collectOutcomes(jobIds)).outcomes);
      // Fehlende Vario-Artikelnummern in Mappings nachfüllen (Punkt 2, self-healing).
      // Fire-and-forget genug, der Job läuft im Hintergrund und die Vario-SKU-Spalte
      // zeigt das Ergebnis nach dem nächsten load()/Tab-Wechsel.
      for (const s of shops) {
        await ctx.supabase.rpc('gambio_enqueue_backfill_mapping_numbers', { _shop_key: s.shop_key });
      }
      // Kategoriebaum je Shop automatisch aktualisieren (Gambio → Vario-Kanal).
      for (const s of shops) {
        const { data, error } = await ctx.supabase.rpc('gambio_enqueue_sync_categories', { _shop_key: s.shop_key });
        if (error) { syncOutcomes.push({ status: 'failed', label: 'Kategorie-Abgleich', error: error.message }); continue; }
        const catJobId = (Array.isArray(data) ? data[0] : data) as string | null;
        if (catJobId) syncOutcomes.push(...(await collectOutcomes([catJobId])).outcomes);
      }
      // Nach dem Items-Pull den Vario-Abgleich neu rechnen - sonst bleiben Match-
      // Suggestions veraltet, wenn sich SKU/EAN eines Produkts geändert hat.
      for (const s of shops) {
        const { data, error } = await ctx.supabase.rpc('gambio_enqueue_match_suggestions', { _shop_key: s.shop_key });
        if (error) { syncOutcomes.push({ status: 'failed', label: 'Trefferermittlung', error: error.message }); continue; }
        const matchJobId = typeof data === 'string' ? data : (data as { job_id?: string } | null)?.job_id;
        if (matchJobId) {
          syncOutcomes.push(...(await collectOutcomes([matchJobId as string])).outcomes);
        }
      }
      // Vario-Bestand frisch ziehen (shop-weit, ohne _id_items) - damit die Spalte
      // „Vario-Bestand" aktuelle Vario-Werte zeigt statt erst beim nächsten 5-Min-Cron.
      for (const s of shops) {
        const { data, error } = await ctx.supabase.rpc('gambio_enqueue_stock_refresh', { _shop_key: s.shop_key });
        if (error) { syncOutcomes.push({ status: 'failed', label: 'Bestandsabgleich', error: error.message }); continue; }
        const stkJobId = typeof data === 'string' ? data : (data as { job_id?: string } | null)?.job_id;
        if (stkJobId) {
          syncOutcomes.push(...(await collectOutcomes([stkJobId as string])).outcomes);
        }
      }
      await load();
      const syncSummary = summarizeBulkOutcome(syncOutcomes, {
        actionLabel: 'Produkt-Abgleich',
        successMessage: mode === 'incremental' ? 'Produkte aktualisiert.' : 'Vollständiger Abgleich abgeschlossen.',
      });
      if (syncSummary.hasFailures) {
        openErrorModal({
          title: 'Produkt-Sync teilweise fehlgeschlagen',
          items: syncSummary.items,
          context: { action: 'gambio_refresh_products', mode, jobIds },
          json: { jobIds, outcomes: syncOutcomes },
        });
      } else {
        showToast(syncSummary.message, 'success');
      }
    } catch (e) {
      openErrorModal({
        title: 'Aktualisieren fehlgeschlagen',
        items: [{ message: (e as Error).message }],
        context: { action: 'gambio_refresh_products', mode },
        json: { message: (e as Error).message, stack: (e as Error).stack },
      });
    } finally {
      refreshBtn.disabled = false;
      fullRefreshBtn.disabled = false;
      activeIcon.classList.remove('fa-spin');
    }
  }

  async function refresh(): Promise<void> {
    await runRefresh('incremental');
  }

  async function assignSelected(): Promise<void> {
    const byShop = new Map<string, string[]>();
    if (!selectAllFiltered) {
      for (const n of api.getSelectedNodes()) {
        // `isMapped`, nicht `varioArticleNumber`: der Variantenhauptartikel hat nie eine eigene
        // Nummer und wuerde sonst mitgeschickt, obwohl seine Kinder laengst verknuepft sind.
        if (!n.data || n.data.isMapped) continue;
        const arr = byShop.get(n.data.storefront) ?? [];
        arr.push(n.data.idItem);
        byShop.set(n.data.storefront, arr);
      }
      if (byShop.size === 0) { showToast('Keine nicht zugeordneten Artikel in der Auswahl.', 'info'); return; }
    }
    const shopsForLog = selectAllFiltered ? [...shopKeys] : [...byShop.keys()];
    assignBtn.disabled = true;
    showToast('Verknüpfen läuft …', 'info');
    try {
      const jobIds: string[] = [];
      if (selectAllFiltered) {
        // Alle Treffer: der FILTER geht an den Server. Eine ID-Liste waere hier unvollstaendig,
        // weil der Browser nur die geladenen Bloecke kennt.
        jobIds.push(...await enqueueByFilter('assign'));
      } else {
        for (const [shopKey, ids] of byShop) {
          const { data, error } = await ctx.supabase.rpc('gambio_enqueue_bulk_assign', { _shop_key: shopKey, _id_items: ids });
          if (error) throw new Error(error.message);
          for (const rec of (Array.isArray(data) ? data : [data])) if (rec?.job_id) jobIds.push(rec.job_id as string);
        }
      }
      if (jobIds.length === 0) { showToast('Kein Verknüpfen möglich - Auswahl ggf. veraltet, bitte aktualisieren.', 'info'); await load(); return; }
      const { outcomes, nochOffen } = await collectOutcomes(jobIds, 900_000, 'Verknüpfen läuft');
      await refreshVarioStocksFor([...byShop].flatMap(([sk, ids]) => ids.map((idItem) => ({ shopKey: sk, idItem }))));
      await load();
      const summary = summarizeBulkOutcome(outcomes, {
        actionLabel: 'Verknüpfen', successMessage: mitOffenenPaketen('Verknüpfen abgeschlossen.', nochOffen),
      });
      if (summary.hasFailures) {
        openErrorModal({
          title: 'Verknüpfen teilweise fehlgeschlagen',
          items: summary.items,
          context: { action: 'gambio_assign_selected', shops: shopsForLog, allHits: selectAllFiltered, jobIds },
          json: { jobIds, outcomes },
        });
      } else {
        showToast(summary.message, 'success');
      }
    } catch (e) {
      openErrorModal({
        title: 'Verknüpfen fehlgeschlagen',
        items: [{ message: (e as Error).message }],
        context: { action: 'gambio_assign_selected', shops: shopsForLog, allHits: selectAllFiltered },
        json: { message: (e as Error).message, stack: (e as Error).stack },
      });
    } finally {
      assignBtn.disabled = false;
    }
  }

  async function seedSelected(): Promise<void> {
    const byShop = new Map<string, string[]>();
    if (!selectAllFiltered) {
      // Nicht zugeordnet UND im Shop aktiv — die Regeln stehen samt Begruendung in
      // `products-seed-eligibility.ts`.
      const zeilen = api.getSelectedNodes().map((n) => n.data).filter((d): d is GridRow => !!d);
      const { anzulegen, uebersprungenInaktiv } = seedKandidaten(zeilen);
      for (const d of anzulegen) {
        const arr = byShop.get(d.storefront) ?? [];
        arr.push(d.idItem);
        byShop.set(d.storefront, arr);
      }
      const hinweis = inaktivHinweis(uebersprungenInaktiv, anzulegen.length);
      if (byShop.size === 0) {
        showToast(hinweis ?? 'Keine nicht zugeordneten Artikel in der Auswahl.', 'info');
        return;
      }
      if (hinweis) showToast(hinweis, 'info');
    }
    const shopsForLog = selectAllFiltered ? [...shopKeys] : [...byShop.keys()];
    seedBtn.disabled = true;
    showToast('Artikel werden in Vario angelegt … (beim ersten Mal inkl. Defaults-Ermittlung, kann kurz dauern)', 'info');
    try {
      const jobIds: string[] = [];
      if (selectAllFiltered) {
        if (filters.status !== 'active') {
          showToast('Es werden nur aktive Artikel angelegt - inaktive bleiben außen vor.', 'info');
        }
        jobIds.push(...await enqueueByFilter('seed'));
      } else {
        for (const [shopKey, ids] of byShop) {
          const { data, error } = await ctx.supabase.rpc('gambio_enqueue_bulk_seed', { _shop_key: shopKey, _id_items: ids, _template_id: null });
          if (error) throw new Error(error.message);
          for (const rec of (Array.isArray(data) ? data : [data])) if (rec?.job_id) jobIds.push(rec.job_id as string);
        }
      }
      if (jobIds.length === 0) { showToast('Kein Anlegen möglich - Auswahl ggf. veraltet, bitte aktualisieren.', 'info'); await load(); return; }
      const { outcomes, nochOffen } = await collectOutcomes(jobIds, 900_000, 'Artikel werden in VARIO angelegt');
      await refreshVarioStocksFor([...byShop].flatMap(([sk, ids]) => ids.map((idItem) => ({ shopKey: sk, idItem }))));
      await load();
      const summary = summarizeBulkOutcome(outcomes, {
        actionLabel: 'Anlegen in VARIO', successMessage: mitOffenenPaketen('Artikel in Vario angelegt.', nochOffen),
      });
      if (summary.hasFailures) {
        openErrorModal({
          title: 'Anlegen teilweise fehlgeschlagen',
          items: summary.items,
          context: { action: 'gambio_seed_selected', shops: shopsForLog, allHits: selectAllFiltered, jobIds },
          json: { jobIds, outcomes },
        });
      } else {
        showToast(summary.message, 'success');
      }
    } catch (e) {
      openErrorModal({
        title: 'Anlegen fehlgeschlagen',
        items: [{ message: (e as Error).message }],
        context: { action: 'gambio_seed_selected', shops: shopsForLog, allHits: selectAllFiltered },
        json: { message: (e as Error).message, stack: (e as Error).stack },
      });
    } finally {
      seedBtn.disabled = false;
    }
  }

  // „Herunterladen" / „Aus Gambio aktualisieren" (Bulk): expliziter Pull G→V für die ausgewählten
  // GEMAPPTEN Zeilen. Kanal-Daten (Name, Beschreibung de+en, Hersteller, Zusatzfelder, Identifier,
  // Kategorien) aus Gambio nach Vario übernehmen (overwrite). Bei Varianten: Hauptartikel + Kinder.
  async function updateFromGambioSelected(): Promise<void> {
    const byShop = new Map<string, string[]>();
    if (!selectAllFiltered) {
      for (const n of api.getSelectedNodes()) {
        if (!n.data || !n.data.varioArticleNumber) continue; // nur gemappte Zeilen
        const arr = byShop.get(n.data.storefront) ?? [];
        arr.push(n.data.idItem);
        byShop.set(n.data.storefront, arr);
      }
      if (byShop.size === 0) { showToast('Keine zugeordneten Artikel in der Auswahl.', 'info'); return; }
    }
    const shopsForLog = selectAllFiltered ? [...shopKeys] : [...byShop.keys()];
    updateFromGambioBtn.disabled = true;
    showToast('Kanal-Daten werden aus Gambio nach Vario übernommen …', 'info');
    try {
      const jobIds: string[] = [];
      if (selectAllFiltered) {
        jobIds.push(...await enqueueByFilter('download'));
      } else {
        for (const [shopKey, ids] of byShop) {
          const { data, error } = await ctx.supabase.rpc('gambio_enqueue_update_from_gambio', { _shop_key: shopKey, _id_items: ids });
          if (error) throw new Error(error.message);
          for (const rec of (Array.isArray(data) ? data : [data])) if (rec?.job_id) jobIds.push(rec.job_id as string);
        }
      }
      if (jobIds.length === 0) { showToast('Nichts zu aktualisieren - Auswahl ggf. nicht (mehr) gemappt.', 'info'); await load(); return; }
      const { outcomes, nochOffen } = await collectOutcomes(jobIds, 900_000, 'Kanal-Daten werden übernommen');
      await load();
      const summary = summarizeBulkOutcome(outcomes, {
        actionLabel: 'Aktualisierung aus Gambio',
        successMessage: mitOffenenPaketen('Daten aus Gambio nach Vario übernommen.', nochOffen),
      });
      if (summary.hasFailures) {
        openErrorModal({
          title: 'Aktualisierung teilweise fehlgeschlagen',
          items: summary.items,
          context: { action: 'gambio_update_from_gambio_selected', shops: shopsForLog, allHits: selectAllFiltered, jobIds },
          json: { jobIds, outcomes },
        });
      } else {
        showToast(summary.message, 'success');
      }
    } catch (e) {
      openErrorModal({
        title: 'Aktualisierung fehlgeschlagen',
        items: [{ message: (e as Error).message }],
        context: { action: 'gambio_update_from_gambio_selected', shops: shopsForLog, allHits: selectAllFiltered },
        json: { message: (e as Error).message, stack: (e as Error).stack },
      });
    } finally {
      updateFromGambioBtn.disabled = false;
    }
  }

  // „Varianten zu Gambio anlegen" (Bulk): für die markierten zugeordneten Vario-Artikel
  // je Shop einen gambio.create_variants_from_vario-Worker-Job anstoßen.
  // Nimmt Vario-Artikel-IDs (nicht Gambio-Item-IDs): nur Zeilen mit varioArticleId berücksichtigen.
  async function createVariantsSelected(): Promise<void> {
    const byShop = new Map<string, string[]>();
    if (!selectAllFiltered) {
      for (const n of api.getSelectedNodes()) {
        if (!n.data || !n.data.varioArticleId) continue;
        const arr = byShop.get(n.data.storefront) ?? [];
        if (!arr.includes(n.data.varioArticleId)) arr.push(n.data.varioArticleId);
        byShop.set(n.data.storefront, arr);
      }
      if (byShop.size === 0) { showToast('Keine zugeordneten Artikel in der Auswahl.', 'info'); return; }
    }
    const shopsForLog = selectAllFiltered ? [...shopKeys] : [...byShop.keys()];
    createVariantsBtn.disabled = true;
    showToast('Variantenanlage wird angestoßen …', 'info');
    try {
      // ID-liefernde RPC (Migration 0333): vorher gab `gambio_enqueue_variant_creation` nur die
      // ANZAHL zurück, die Aktion meldete deshalb bloß „N Jobs angestoßen" und das Ergebnis erfuhr
      // der Anwender nie — ein Lauf, der an jedem Artikel scheiterte, sah wie ein Erfolg aus.
      const jobIds: string[] = [];
      if (selectAllFiltered) {
        // Der Server bildet die Treffer selbst auf DISTINCT Vario-Artikel-IDs ab (Migration 0345)
        // - sonst reiht ein Variantenpaket mehrfach ein, einmal je Kind-Zeile.
        jobIds.push(...await enqueueByFilter('create_variants'));
      } else {
        for (const [shopKey, ids] of byShop) {
          const { data, error } = await ctx.supabase.rpc('gambio_enqueue_variant_creation_jobs', { _shop_key: shopKey, _vario_article_ids: ids });
          if (error) throw new Error(error.message);
          for (const id of (Array.isArray(data) ? data : [data])) if (typeof id === 'string') jobIds.push(id);
        }
      }
      if (jobIds.length === 0) { showToast('Keine Variantenanlage angestoßen.', 'info'); return; }
      const { outcomes, nochOffen } = await collectOutcomes(jobIds, 900_000, 'Varianten werden angelegt');
      await load();
      const summary = summarizeBulkOutcome(outcomes, {
        actionLabel: 'Variantenanlage',
        successMessage: mitOffenenPaketen(`Variantenanlage abgeschlossen (${jobIds.length} Job(s)).`, nochOffen),
        itemNoun: 'Varianten',
      });
      if (summary.hasFailures) {
        openErrorModal({
          title: 'Variantenanlage teilweise fehlgeschlagen',
          intro: 'Einzelheiten je Artikel; der volle Wortlaut steht im Protokoll.',
          items: summary.items,
          context: { action: 'gambio_create_variants_selected', shops: shopsForLog, allHits: selectAllFiltered, jobIds },
          json: { jobIds, outcomes },
        });
      } else {
        showToast(summary.message, 'success');
      }
    } catch (e) {
      openErrorModal({
        title: 'Variantenanlage fehlgeschlagen',
        items: [{ message: (e as Error).message }],
        context: { action: 'gambio_create_variants_selected', shops: shopsForLog, allHits: selectAllFiltered },
        json: { message: (e as Error).message, stack: (e as Error).stack },
      });
    } finally {
      createVariantsBtn.disabled = false;
    }
  }

  /** Ergebnis eines Sammel-Wartelaufs. `nochOffen` sind Jobs, die schlicht noch laufen. */
  interface BulkWarteErgebnis { outcomes: BulkJobOutcome[]; nochOffen: number }

  /**
   * Wartet Jobs ab und sammelt sie als `BulkJobOutcome` ein (inkl. Schritt-Label aus dem Job-Typ).
   * Ersetzt das alte `anyFailed = job.status !== 'completed'`: das hielt einen Job, der durchlief
   * und dabei Artikel verlor (`failed > 0`), für einen vollen Erfolg (User-Befund 2026-08-21).
   * Wirft nicht.
   *
   * **Nebenläufig statt nacheinander.** Vorher lief die Schleife Job für Job — bei 22 Paketen sah
   * der Anwender minutenlang nichts, und ein Zähler hätte auf 0 gestanden, solange das erste Paket
   * dauert. Jetzt wartet jeder Job für sich, und `onFortschritt` meldet nach jedem abgeschlossenen
   * Paket den Stand. Die Reihenfolge der Ergebnisse bleibt über `Promise.all` erhalten.
   *
   * **Eine Zeitüberschreitung ist KEIN Fehlschlag.** Vorher wurde sie als `status: 'failed'`
   * verbucht, und die Zusammenfassung meldete Fehler, die es nicht gab: bei 22 Paketen laufen
   * höchstens zehn gleichzeitig, die übrigen stehen in der Schlange und waren nach dem alten
   * 180-Sekunden-Fenster zwangsläufig „gescheitert". Solche Jobs zählen jetzt als `nochOffen` —
   * der Worker arbeitet sie weiter ab, das Ergebnis steht im Protokoll.
   */
  /**
   * Artikelzahl je Job aus dem Payload. Nur Jobs mit einer `units`-Liste (Anlegen, Zuordnen,
   * Aktualisieren) liefern eine; fehlt sie, bleibt es beim Paket-Zähler. Eine Abfrage statt
   * Raten: die Trefferzahl aus der Oberfläche wäre falsch, weil eine Variantenfamilie IMMER
   * vollständig angelegt wird — auch die Geschwister, die der Filter nicht getroffen hat.
   */
  async function ladeUnitZahlen(jobIds: ReadonlyArray<string>): Promise<Map<string, number>> {
    const zahlen = new Map<string, number>();
    try {
      const { data } = await ctx.supabase.schema('account').from('jobs')
        .select('id, payload').in('id', jobIds as string[]);
      for (const row of (data ?? []) as Array<{ id: string; payload: unknown }>) {
        const units = (row.payload as { units?: unknown } | null)?.units;
        if (Array.isArray(units)) zahlen.set(row.id, units.length);
      }
    } catch { /* ohne Artikelzahl bleibt der Paket-Zaehler - kein Grund, die Aktion zu stoppen */ }
    return zahlen;
  }

  async function collectOutcomes(
    jobIds: ReadonlyArray<string>,
    timeoutMs = 900_000,
    fortschrittsText?: string,
  ): Promise<BulkWarteErgebnis> {
    // Artikelzahlen vorab holen, damit der Zähler von Anfang an die richtige Gesamtzahl nennt.
    const unitsJeJob = fortschrittsText ? await ladeUnitZahlen(jobIds) : new Map<string, number>();
    const artikelGesamt = [...unitsJeJob.values()].reduce((a, b) => a + b, 0);
    const verarbeitet = new Map<string, number>();
    // Wartet ein Paket auf etwas (VARIO legt die Varianten noch an, Vorgaben werden ermittelt),
    // steht der Grund hier. Ohne das sah der Betreiber zweiundzwanzig Minuten lang nur den
    // Spinner, obwohl der Grund die ganze Zeit in der Job-Zeile stand (Pfannenshop 22.09.2026).
    const warten = new Map<string, string>();
    let fertigeJobs = 0;

    const anzeige = fortschrittsText ? showProgressToast(fortschrittsText) : null;
    const zeige = (): void => {
      if (!anzeige) return;
      const artikel = [...verarbeitet.values()].reduce((a, b) => a + b, 0);
      const teile = [fortschrittsText!];
      if (artikelGesamt > 0) teile.push(`${artikel} von ${artikelGesamt} Artikeln`);
      teile.push(jobIds.length === 1 ? '1 Paket' : `Paket ${fertigeJobs} von ${jobIds.length}`);
      // Der Wartegrund ersetzt die Zahlen nicht, er tritt daneben: die Zahlen sagen, wie weit der
      // Lauf ist, der Grund sagt, warum es gerade nicht weitergeht.
      const grund = [...warten.values()][0];
      anzeige.update(grund ? `${teile.join(' · ')} — ${grund}` : teile.join(' · '));
    };
    zeige();

    const roh = await Promise.all(jobIds.map(async (id): Promise<BulkJobOutcome | null> => {
      try {
        // 2 s statt der 500 ms Standard: bei zwei Dutzend gleichzeitigen Wartenden waeren das
        // sonst rund vierzig Abfragen je Sekunde, nur um zuzusehen.
        const job = await awaitJob(ctx.supabase, id, {
          timeoutMs,
          pollIntervalMs: 2_000,
          onProgress: (row) => {
            // Wartegrund zuerst: er aendert sich unabhaengig von der Artikelzahl, und bei einem
            // Paket, das auf VARIO wartet, bewegt sich `processed` gerade gar nicht.
            const hinweis = wartehinweis(row);
            const vorher = warten.get(id) ?? null;
            if (hinweis !== vorher) {
              if (hinweis) warten.set(id, hinweis); else warten.delete(id);
              zeige();
            }
            const pr = row.progress as { processed?: unknown } | null;
            const n = typeof pr?.processed === 'number' ? pr.processed : null;
            if (n === null) return;
            if (verarbeitet.get(id) === n) return;
            verarbeitet.set(id, n);
            zeige();
          },
        });
        // Fertig heisst: alle Artikel des Pakets sind durch, auch wenn der letzte
        // Zwischenstand aelter ist.
        verarbeitet.set(id, unitsJeJob.get(id) ?? verarbeitet.get(id) ?? 0);
        const label = jobStepLabel(job.type);
        return { status: job.status, error: job.error, result: job.result, ...(label !== undefined ? { label } : {}) };
      } catch (e) {
        const msg = (e as Error).message;
        // Genau die Meldung, die awaitJob beim Ablaufen des Fensters wirft - alles andere ist ein
        // echter Fehler (z. B. eine gescheiterte Abfrage) und wird auch so gemeldet.
        if (msg.includes('did not finish within')) return null;
        return { status: 'failed', error: msg };
      } finally {
        fertigeJobs += 1;
        warten.delete(id);
        zeige();
      }
    }));

    anzeige?.close();
    return {
      outcomes: roh.filter((o): o is BulkJobOutcome => o !== null),
      nochOffen: roh.filter((o) => o === null).length,
    };
  }

  /** Hängt an eine Erfolgsmeldung den Hinweis auf noch laufende Pakete an. */
  function mitOffenenPaketen(text: string, nochOffen: number): string {
    if (nochOffen === 0) return text;
    return `${text} · ${nochOffen} ${nochOffen === 1 ? 'Paket läuft' : 'Pakete laufen'} noch — das Ergebnis steht im Protokoll.`;
  }

  // Export zu Gambio: Vario führt - Bestand/Preis/Sonderpreis/Bilder der zugeordneten
  // Artikel (forced) nach Gambio pushen. Pro Shop ein gambio_enqueue_export-Aufruf (ohne
  // _kinds → Default inkl. 'images' → gambio.push_images) → liefert je kind eine Job-ID;
  // auf Abschluss aller Jobs warten (Status prüfen) und zusammenfassen.
  async function exportToGambio(): Promise<void> {
    exportBtn.disabled = true;
    exportIcon.classList.add('fa-spin');
    try {
      const shopsRes = await ctx.supabase.rpc('gambio_list_shops');
      const shops = (Array.isArray(shopsRes.data) ? shopsRes.data : []) as Array<{ shop_key: string }>;
      if (shops.length === 0) { showToast('Kein Gambio-Shop vorhanden.', 'info'); return; }
      showToast('Export zu Gambio läuft … (Bestand, Preis, Sonderpreis, Bilder)', 'info');
      const jobIds: string[] = [];
      for (const s of shops) {
        const { data, error } = await ctx.supabase.rpc('gambio_enqueue_export', { _shop_key: s.shop_key });
        if (error) throw new Error(error.message);
        // returns setof uuid → Array von Strings.
        for (const id of (Array.isArray(data) ? data : [data])) if (typeof id === 'string') jobIds.push(id);
      }
      if (jobIds.length === 0) { showToast('Kein Export angestoßen.', 'info'); return; }
      // Ergebnis je Job einsammeln. Die Push-Jobs führen keine Einzelfehler mit, nur Zähler —
      // deshalb der Schritt-Name (Bestand/Preis/Sonderpreis/Bilder) als Label. Vorher wurde nur
      // `status !== 'completed'` geprüft: ein Job, der durchlief und dabei Artikel verlor
      // (`failed > 0`), galt als voller Erfolg (User-Befund 2026-08-21).
      const { outcomes, nochOffen } = await collectOutcomes(jobIds, 900_000, 'Export zu Gambio läuft');
      await load();
      const summary = summarizeBulkOutcome(outcomes, {
        actionLabel: 'Export zu Gambio',
        successMessage: mitOffenenPaketen(`Export zu Gambio abgeschlossen (${jobIds.length} Jobs).`, nochOffen),
      });
      if (summary.hasFailures) {
        openErrorModal({
          title: 'Export teilweise fehlgeschlagen',
          intro: 'Einzelheiten je Schritt; der volle Wortlaut steht im Protokoll.',
          items: summary.items,
          context: { action: 'gambio_export_to_gambio', jobIds },
          json: { jobIds, outcomes },
        });
      } else {
        showToast(summary.message, 'success');
      }
    } catch (e) {
      openErrorModal({
        title: 'Export fehlgeschlagen',
        items: [{ message: (e as Error).message }],
        context: { action: 'gambio_export_to_gambio' },
        json: { message: (e as Error).message, stack: (e as Error).stack },
      });
    } finally {
      exportBtn.disabled = false;
      exportIcon.classList.remove('fa-spin');
    }
  }

  /** Löst die Verknüpfung eines einzelnen Gambio-Produkts mit seinem Vario-Artikel auf. */
  async function unassignOne(shopKey: string, idItem: string): Promise<void> {
    try {
      const { data, error } = await ctx.supabase.rpc('gambio_enqueue_unassign_vario_listing', {
        _shop_key: shopKey,
        _id_item: idItem,
      });
      if (error) throw new Error(error.message);
      const jobId = typeof data === 'string' ? data : (data as { job_id?: string } | null)?.job_id;
      if (jobId) {
        showToast('Artikel wird aus dem Gambio-Kanal genommen …', 'info');
        const summary = summarizeBulkOutcome((await collectOutcomes([jobId], 60_000)).outcomes, {
          actionLabel: 'Aus dem Gambio-Kanal nehmen', successMessage: 'Aus dem Gambio-Kanal genommen.',
        });
        if (summary.hasFailures) {
          openErrorModal({
            title: 'Aus dem Gambio-Kanal nehmen fehlgeschlagen',
            items: summary.items,
            context: { action: 'gambio_unassign_vario_listing', shopKey, idItem, jobId },
            json: { jobId, items: summary.items },
          });
        } else {
          showToast(summary.message, 'success');
        }
      }
      // Match neu rechnen - der Vario-Artikel existiert weiter, also soll die Zeile
      // „Zuordnen" (Treffer) statt „Anlegen" zeigen.
      await refreshMatchFor([{ shopKey, idItem }]);
      await load();
    } catch (e) {
      openErrorModal({
        title: 'Aus dem Gambio-Kanal nehmen fehlgeschlagen',
        items: [{ message: (e as Error).message }],
        context: { action: 'gambio_unassign_vario_listing', shopKey, idItem },
        json: { message: (e as Error).message, stack: (e as Error).stack },
      });
      await load();
    }
  }

  /**
   * Bulk-Modal für Min/Max-Bestand. Leeres Feld = COALESCE (diese Spalte unverändert).
   * Nur für gemappte Rows (varioArticleNumber != null). KEIN window.confirm/alert.
   */
  async function openMinMaxBulkModal(mappedRows: GridRow[] | null): Promise<void> {
    // `null` = alle Treffer des aktuellen Filters. Welche davon zugeordnet sind, weiss nur der
    // Server; nicht zugeordnete ueberspringt er (Migration 0345).
    const allHits = mappedRows === null;
    const total = allHits ? totalRows : api.getSelectedNodes().length;
    const mapped = allHits ? total : mappedRows.length;
    const skipped = allHits ? 0 : total - mapped;

    return new Promise((resolve) => {
      const body = `
        <div class="kl-modal-meta">
          <strong>${total} ${total === 1 ? 'Produkt' : 'Produkte'}</strong>
          <div class="text-grey">${allHits
            ? 'Alle Treffer des aktuellen Filters · nicht zugeordnete Artikel werden übersprungen'
            : `${mapped} zugeordnet${skipped > 0 ? ` · ${skipped} nicht zugeordnet (werden übersprungen)` : ''}`}</div>
        </div>
        <div class="kl-form-row">
          <label for="gm-mm-min">Min-Bestand</label>
          <div style="display:flex;align-items:center;gap:10px;flex:1;">
            <div class="v-input" style="flex:1;"><input id="gm-mm-min" type="number" min="0" step="1" inputmode="numeric" placeholder="leer = unverändert" /></div>
            <label style="display:inline-flex;align-items:center;gap:6px;white-space:nowrap;cursor:pointer;"><input type="checkbox" id="gm-mm-min-clear" /> löschen</label>
          </div>
        </div>
        <div class="kl-form-row">
          <label for="gm-mm-max">Max-Bestand</label>
          <div style="display:flex;align-items:center;gap:10px;flex:1;">
            <div class="v-input" style="flex:1;"><input id="gm-mm-max" type="number" min="0" step="1" inputmode="numeric" placeholder="leer = unverändert" /></div>
            <label style="display:inline-flex;align-items:center;gap:6px;white-space:nowrap;cursor:pointer;"><input type="checkbox" id="gm-mm-max-clear" /> löschen</label>
          </div>
        </div>
        <div class="kl-form-hint">Leeres Feld = unverändert · Zahl = setzen · „löschen" entfernt den Wert (Min/Max-Logik aus). Min/Max sind Vario-Einstellungen - der Bestands-Sync wertet sie aus.</div>
        <div class="kl-modal-error" id="gm-mm-error" hidden></div>
      `;
      const footer = `
        <button type="button" class="v-button" data-modal-cancel>Abbrechen</button>
        <button type="button" class="v-button v-button-primary" data-action="apply" disabled>
          <span class="v-icon fa-regular fa-check"></span> Übernehmen
        </button>
      `;

      let settled = false;
      const finish = () => { if (settled) return; settled = true; resolve(); };

      const modal = openModal({
        title: 'Min/Max-Bestand',
        icon: 'fa-layer-group',
        width: 460,
        body,
        footer,
        onClose: finish,
      });

      const minInp = modal.el.querySelector<HTMLInputElement>('#gm-mm-min')!;
      const maxInp = modal.el.querySelector<HTMLInputElement>('#gm-mm-max')!;
      const minClearCb = modal.el.querySelector<HTMLInputElement>('#gm-mm-min-clear')!;
      const maxClearCb = modal.el.querySelector<HTMLInputElement>('#gm-mm-max-clear')!;
      const errEl = modal.el.querySelector<HTMLDivElement>('#gm-mm-error')!;
      const applyBtn = modal.el.querySelector<HTMLButtonElement>('[data-action="apply"]')!;

      // '' → null (Spalte unverändert lassen); gültige Ganzzahl ≥ 0 → Zahl; sonst undefined (ungültig).
      const parse = (v: string): number | null | undefined => {
        const t = v.trim();
        if (t === '') return null;
        const n = Number.parseInt(t, 10);
        if (Number.isNaN(n) || n < 0) return undefined;
        return n;
      };

      // Effektive Intent pro Feld: clear (→ NULL leeren) | number (setzen) | null (unverändert) | undefined (ungültig).
      const intent = (inp: HTMLInputElement, clr: HTMLInputElement): number | null | undefined =>
        clr.checked ? null : parse(inp.value);

      const validate = () => {
        // „leeren" sperrt das zugehörige Zahlenfeld (Wert wird ignoriert).
        minInp.disabled = minClearCb.checked;
        maxInp.disabled = maxClearCb.checked;
        const minClr = minClearCb.checked;
        const maxClr = maxClearCb.checked;
        const min = intent(minInp, minClearCb);
        const max = intent(maxInp, maxClearCb);
        let error: string | null = null;
        if (mapped === 0) {
          error = 'Keines der markierten Produkte ist zugeordnet - Min/Max gibt es nur für zugeordnete Artikel.';
        } else if ((!minClr && min === undefined) || (!maxClr && max === undefined)) {
          error = 'Bitte ganze Zahlen ≥ 0 eingeben.';
        } else if (typeof min === 'number' && typeof max === 'number' && min > max) {
          error = `Min (${min}) darf nicht größer als Max (${max}) sein.`;
        }
        // Mind. eine Aktion: setzen (Zahl) ODER leeren - auf einem der beiden Felder.
        const minAction = minClr || (typeof min === 'number');
        const maxAction = maxClr || (typeof max === 'number');
        const valuesOk = min !== undefined && max !== undefined && (minAction || maxAction);
        applyBtn.disabled = !(error === null && valuesOk);
        errEl.hidden = error === null;
        if (error) errEl.textContent = error;
      };
      minInp.addEventListener('input', validate);
      maxInp.addEventListener('input', validate);
      minClearCb.addEventListener('change', validate);
      maxClearCb.addEventListener('change', validate);
      validate();

      applyBtn.addEventListener('click', () => {
        const minClr = minClearCb.checked;
        const maxClr = maxClearCb.checked;
        const min = intent(minInp, minClearCb);
        const max = intent(maxInp, maxClearCb);
        if (min === undefined || max === undefined) return;
        const minAction = minClr || (typeof min === 'number');
        const maxAction = maxClr || (typeof max === 'number');
        if (!minAction && !maxAction) return;
        applyBtn.disabled = true;
        applyBtn.innerHTML = '<span class="v-icon fa-regular fa-spinner-third fa-spin"></span> Übernehme …';

        void (async () => {
          const parts: string[] = [];
          if (minClr) parts.push('Min gelöscht');
          else if (typeof min === 'number') parts.push(`Min ${min}`);
          if (maxClr) parts.push('Max gelöscht');
          else if (typeof max === 'number') parts.push(`Max ${max}`);

          if (allHits) {
            // Alle Treffer: der Filter geht an den Server. Die RPC liefert fuer 'min_max' keine
            // Job-Kennungen (sie schreibt direkt), deshalb keine Job-Auswertung.
            try {
              await enqueueByFilter('min_max', { _min: min, _max: max, _clear_min: minClr, _clear_max: maxClr });
              await load();
              showToast(`${parts.join(' · ')} für alle ${total} Treffer übernommen.`, 'success');
            } catch (ex) {
              openErrorModal({
                title: 'Min/Max konnte nicht gesetzt werden',
                items: [{ message: (ex as Error).message }],
                context: { action: 'gambio_bulk_set_listing_min_max', allHits: true, shops: shopKeys },
                json: { message: (ex as Error).message },
              });
            }
            modal.close();
            finish();
            return;
          }

          // Gruppiere gemappte Rows per Storefront.
          const byStorefront = new Map<string, string[]>();
          for (const r of mappedRows ?? []) {
            const arr = byStorefront.get(r.storefront) ?? [];
            arr.push(r.idItem);
            byStorefront.set(r.storefront, arr);
          }
          let updated = 0;
          let anyError = false;
          for (const [storefront, idItems] of byStorefront) {
            const { data, error } = await ctx.supabase.rpc('gambio_bulk_set_listing_min_max', {
              _storefront: storefront,
              _id_items: idItems,
              _min: min,
              _max: max,
              _clear_min: minClr,
              _clear_max: maxClr,
            });
            if (error) {
              openErrorModal({
                title: 'Min/Max konnte nicht gesetzt werden',
                items: [{ message: error.message }],
                context: { action: 'gambio_bulk_set_listing_min_max', storefront, idItems },
                json: error,
              });
              anyError = true;
            } else {
              updated += typeof data === 'number' ? data : Number(data ?? 0);
            }
          }
          await load();
          if (!anyError && updated > 0) {
            showToast(
              `${parts.join(' · ')} für ${updated} ${updated === 1 ? 'Produkt' : 'Produkte'}${skipped > 0 ? ` · ${skipped} übersprungen` : ''}.`,
              'success',
            );
          }
          modal.close();
          finish();
        })();
      });

      setTimeout(() => minInp.focus(), 50);
    });
  }

  // Delegierter Click-Handler auf dem Grid-Host: fängt Klicks auf die
  // Aktions-Buttons in der „Aktionen"-Spalte ab (AG Grid rendert Cell-Renderer-
  // HTML direkt in die DOM-Zellen → Event-Delegation ist der idiomatische Weg).
  gridHost.addEventListener('click', (e) => {
    const target = e.target as HTMLElement;
    const btn = target.closest<HTMLElement>('[data-act]');
    if (!btn) return;
    const act = btn.dataset['act'];
    const key = btn.dataset['key'] ?? '';
    const sf = btn.dataset['sf'] ?? '';
    const id = btn.dataset['id'] ?? '';
    if (act === 'edit-price') {
      // Preis-Zelle (Anchor) → Kundengruppen-/Staffelpreis-Modal. preventDefault, damit der
      // Anchor nicht die Hash-Route (/#/products) verändert.
      e.preventDefault();
      const item = btn.dataset['item'] ?? '';
      if (item && sf) void openPriceModal(ctx, item, sf, () => void load());
    } else if (act === 'edit-variant-price') {
      e.preventDefault();
      const item = btn.dataset['item'] ?? '';
      // Zeile ueber die Grid-Id holen und das Familien-Listing aus der Zeile selbst nehmen -
      // frueher zwei Suchen ueber ALLE Zeilen (Klick-Zeile und Elternzeile).
      const clicked = api.getRowNode(`${sf}::${item}`)?.data ?? null;
      if (!clicked) { showToast('Variantenzeile nicht gefunden.', 'error'); return; }
      const listingId = clicked.parentListingId ?? clicked.gambioListingId ?? null;
      if (!listingId) { showToast('Familien-Listing nicht auflösbar — bitte in der Variantenansicht bearbeiten.', 'error'); return; }
      void openVariantPriceModal(ctx, clicked, listingId, () => void load());
    } else if (act === 'unassign') {
      // Erster Klick: in den Bestätigungszustand wechseln und Zelle neu rendern.
      confirmingUnassign.add(key);
      api.refreshCells({ force: true });
    } else if (act === 'unassign-cancel') {
      confirmingUnassign.delete(key);
      api.refreshCells({ force: true });
    } else if (act === 'unassign-confirm') {
      confirmingUnassign.delete(key);
      void unassignOne(sf, id);
    } else if (act === 'assign-row') {
      btn.setAttribute('disabled', '');
      btn.innerHTML = '<span class="v-icon fa-regular fa-spinner-third fa-spin"></span>';
      void (async () => {
        try {
          const { data, error } = await ctx.supabase.rpc('gambio_enqueue_bulk_assign', {
            _shop_key: sf,
            _id_items: [id],
          });
          if (error) throw new Error(error.message);
          const rec = Array.isArray(data) ? data[0] : data;
          const jobId = rec?.job_id as string | undefined;
          if (!jobId) { showToast('Keine Zuordnung angestoßen - Suggestion ggf. veraltet, bitte Abgleich aktualisieren.', 'info'); await load(); return; }
          const job = await awaitJob(ctx.supabase, jobId, 180_000);
          const rowSummary = summarizeBulkOutcome(
            [{ status: job.status, error: job.error, result: job.result, ...(jobStepLabel(job.type) !== undefined ? { label: jobStepLabel(job.type)! } : {}) }],
            { actionLabel: 'Zuordnung', successMessage: 'Zuordnung abgeschlossen.' },
          );
          if (rowSummary.hasFailures) {
            openErrorModal({
              title: 'Verknüpfen fehlgeschlagen',
              items: rowSummary.items,
              context: { action: 'gambio_assign_row', shopKey: sf, idItem: id, jobId },
              json: job,
            });
          } else {
            const result = job.result as { assigned?: number } | null;
            if (typeof result?.assigned === 'number' && result.assigned === 0) {
              showToast('Kein Vario-Treffer (bitte Abgleich aktualisieren).', 'info');
            } else {
              showToast('Zuordnung abgeschlossen.', 'success');
            }
          }
          await refreshVarioStocksFor([{ shopKey: sf, idItem: id }]);
          await load();
        } catch (ex) {
          openErrorModal({
            title: 'Verknüpfen fehlgeschlagen',
            items: [{ message: (ex as Error).message }],
            context: { action: 'gambio_assign_row', shopKey: sf, idItem: id },
            json: { message: (ex as Error).message, stack: (ex as Error).stack },
          });
          await load();
        }
      })();
    } else if (act === 'seed-row') {
      btn.setAttribute('disabled', '');
      btn.innerHTML = '<span class="v-icon fa-regular fa-spinner-third fa-spin"></span>';
      void (async () => {
        try {
          const { data, error } = await ctx.supabase.rpc('gambio_enqueue_bulk_seed', {
            _shop_key: sf,
            _id_items: [id],
            _template_id: null,
          });
          if (error) throw new Error(error.message);
          const rec = Array.isArray(data) ? data[0] : data;
          const jobId = rec?.job_id as string | undefined;
          if (!jobId) { showToast('Kein Anlegen angestoßen - bitte aktualisieren.', 'info'); await load(); return; }
          const rowSummary = summarizeBulkOutcome((await collectOutcomes([jobId])).outcomes, {
            actionLabel: 'Anlegen in VARIO', successMessage: 'Artikel in Vario angelegt.',
          });
          if (rowSummary.hasFailures) {
            openErrorModal({
              title: 'Anlegen fehlgeschlagen',
              items: rowSummary.items,
              context: { action: 'gambio_seed_row', shopKey: sf, idItem: id, jobId },
              json: { jobId, items: rowSummary.items },
            });
          } else {
            showToast(rowSummary.message, 'success');
          }
          await refreshVarioStocksFor([{ shopKey: sf, idItem: id }]);
          await load();
        } catch (ex) {
          openErrorModal({
            title: 'Anlegen fehlgeschlagen',
            items: [{ message: (ex as Error).message }],
            context: { action: 'gambio_seed_row', shopKey: sf, idItem: id },
            json: { message: (ex as Error).message, stack: (ex as Error).stack },
          });
          await load();
        }
      })();
    } else if (act === 'refresh-from-gambio') {
      btn.setAttribute('disabled', '');
      btn.innerHTML = '<span class="v-icon fa-regular fa-spinner-third fa-spin"></span>';
      void (async () => {
        try {
          const { data, error } = await ctx.supabase.rpc('gambio_enqueue_update_from_gambio', { _shop_key: sf, _id_items: [id] });
          if (error) throw new Error(error.message);
          const jobIds = (Array.isArray(data) ? data : [data]).map((r) => r?.job_id).filter((x): x is string => !!x);
          if (jobIds.length === 0) { showToast('Nichts zu aktualisieren.', 'info'); await load(); return; }
          const { outcomes } = await collectOutcomes(jobIds);
          await load();
          const summary = summarizeBulkOutcome(outcomes, {
            actionLabel: 'Aktualisierung aus Gambio', successMessage: 'Aus Gambio aktualisiert.',
          });
          if (summary.hasFailures) {
            openErrorModal({
              title: 'Aktualisierung fehlgeschlagen',
              items: summary.items,
              context: { action: 'gambio_refresh_from_gambio_row', shopKey: sf, idItem: id, jobIds },
              json: { jobIds, outcomes },
            });
          } else {
            showToast(summary.message, 'success');
          }
        } catch (ex) {
          openErrorModal({
            title: 'Aktualisierung fehlgeschlagen',
            items: [{ message: (ex as Error).message }],
            context: { action: 'gambio_refresh_from_gambio_row', shopKey: sf, idItem: id },
            json: { message: (ex as Error).message, stack: (ex as Error).stack },
          });
          await load();
        }
      })();
    } else if (act === 'upload-to-gambio') {
      const vaid = btn.dataset['vaid'];
      if (!vaid) return;
      btn.setAttribute('disabled', '');
      void (async () => {
        try {
          const { error } = await ctx.supabase.rpc('gambio_enqueue_publish_or_update', { _shop_key: sf, _vario_article_ids: [vaid] });
          if (error) throw new Error(error.message);
          showToast('Übertragung zu Gambio gestartet.', 'success');
        } catch (ex) {
          openErrorModal({
            title: 'Übertragung fehlgeschlagen',
            items: [{ message: (ex as Error).message }],
            context: { action: 'gambio_upload_to_gambio', shopKey: sf, idItem: id, vaid },
            json: { message: (ex as Error).message, stack: (ex as Error).stack },
          });
        } finally {
          btn.removeAttribute('disabled');
        }
      })();
    } else if (act === 'toggle-active') {
      const rkey = `${sf}::${id}`;
      if (togglingActive.has(rkey)) return;
      const node = api.getRowNode(rkey) ?? null;
      const row = node?.data;
      if (!node || !row || row.isActive === null) return;
      const next = !(row.isActive === true);
      // Optimistisch flippen; der In-Flight-Guard disabled den Switch beim Re-Render.
      togglingActive.add(rkey);
      row.isActive = next;
      row.effectiveIsActive = next;
      // Kinder folgen dem Elternprodukt. `forEachNode` laeuft ueber die GELADENEN Zeilen — hier
      // geht es nur ums Neuzeichnen des Sichtbaren, nicht ums Auffinden von Daten.
      if (row.isVariantMain) api.forEachNode((n) => {
        if (n.data && n.data.storefront === sf && n.data.isVariant
            && n.data.idItem.replace(/-v\d+$/, '') === id) n.data.effectiveIsActive = next;
      });
      // Beim Hauptartikel ALLE Zellen neu zeichnen, damit die Frontend-Links der Kinder (die dem
      // Vater-Status folgen) mit umschalten; sonst genügt die eigene Zeile.
      if (row.isVariantMain) api.refreshCells({ force: true });
      else api.refreshCells({ rowNodes: [node], force: true });
      void (async () => {
        try {
          const res = await fetch(`${ctx.supabaseUrl}/functions/v1/gambio-product-active`, {
            method: 'POST',
            headers: { Authorization: `Bearer ${ctx.session.token}`, 'Content-Type': 'application/json' },
            body: JSON.stringify({ shopKey: sf, action: 'set', productId: id, isActive: next }),
          });
          const out = (await res.json().catch(() => ({}))) as { ok?: boolean; error?: { message?: string } };
          if (!res.ok || !out.ok) throw new Error(out.error?.message ?? `HTTP ${res.status}`);
          showToast(next ? 'Produkt im Gambio-Shop aktiviert.' : 'Produkt im Gambio-Shop deaktiviert.', 'success');
        } catch (ex) {
          row.isActive = !next; // Revert bei Fehler
          row.effectiveIsActive = !next;
          if (row.isVariantMain) api.forEachNode((n) => {
            if (n.data && n.data.storefront === sf && n.data.isVariant
                && n.data.idItem.replace(/-v\d+$/, '') === id) n.data.effectiveIsActive = !next;
          });
          openErrorModal({
            title: 'Umschalten fehlgeschlagen',
            items: [{ message: (ex as Error).message }],
            context: { action: 'gambio_product_active', shopKey: sf, idItem: id },
            json: { message: (ex as Error).message, stack: (ex as Error).stack },
          });
        } finally {
          togglingActive.delete(rkey);
          if (row.isVariantMain) api.refreshCells({ force: true });
          else api.refreshCells({ rowNodes: [node!], force: true });
          // Der Status-Filter laeuft jetzt in der Datenbank: bei gesetztem Filter kann die Zeile
          // durch das Umschalten herausfallen, dann muessen die Bloecke neu geholt werden.
          if (filters.status) api.refreshInfiniteCache();
        }
      })();
    }
  });

  // --- Bulk-Leiste-Aktionen ---
  assignBtn.addEventListener('click', () => void assignSelected());
  seedBtn.addEventListener('click', () => void seedSelected());
  updateFromGambioBtn.addEventListener('click', () => void updateFromGambioSelected());
  createVariantsBtn.addEventListener('click', () => void createVariantsSelected());
  importPricesBtn.addEventListener('click', () => {
    const selectedRows = api.getSelectedNodes()
      .map((n) => n.data)
      .filter((d): d is GridRow => !!d);
    void importPricesFromGambio(ctx, selectedRows, () => void load());
  });
  bulkClearBtn.addEventListener('click', () => {
    selectAllFiltered = false;
    api.deselectAll();
    updateBulk();
  });
  // Ein Klick: alle Treffer des aktuellen Filters. Vorhandene Haken fallen dabei weg, damit die
  // Zeilen-Haken nicht etwas anderes behaupten als die Auswahlleiste.
  selAllBtn.addEventListener('click', () => {
    selectAllFiltered = true;
    // Erst die geladenen Zeilen abhaken (sichtbare Rueckmeldung), dann die Leiste nachziehen.
    hakeGeladeneZeilenAb();
    updateBulk();
  });
  minmaxBtn.addEventListener('click', () => {
    if (selectAllFiltered) { void openMinMaxBulkModal(null); return; }
    const mappedRows = api.getSelectedNodes()
      .map((n) => n.data)
      // Nur gemappte FLACHE/Varianten-Produkte - Hauptartikel sind nicht bestandsgeführt.
      .filter((d): d is GridRow => !!d && !!d.varioArticleNumber);
    void openMinMaxBulkModal(mappedRows);
  });

  // --- Header-Aktionen: Refresh (inkrementell + vollständig) + Export ---
  refreshBtn.addEventListener('click', () => void refresh());
  fullRefreshBtn.addEventListener('click', () => void runRefresh('full'));
  exportBtn.addEventListener('click', () => void exportToGambio());

  // --- Filterleiste: Suchfeld, Status, Mapping ---
  const searchInput = host.querySelector<HTMLInputElement>('#gambio-products-search');
  const statusSelect = host.querySelector<HTMLSelectElement>('#gambio-products-status');
  const mappingSelect = host.querySelector<HTMLSelectElement>('#gambio-products-mapping');

  let searchDebounce: number | null = null;
  searchInput?.addEventListener('input', () => {
    if (searchDebounce !== null) window.clearTimeout(searchDebounce);
    searchDebounce = window.setTimeout(() => {
      filters.search = searchInput.value.trim();
      persistFilters();
      applyFilters();
      // 450 statt 250 ms: die Suche laeuft jetzt auf dem Server (rund 250 ms je Abfrage). Bei
      // 250 ms Wartezeit setzt ein normaler Schreibfluss noch waehrend der laufenden Abfrage nach,
      // und die Liste baut sich gefuehlt nach jedem Buchstaben neu auf (Betreiber 27.08.2026).
    }, 450) as unknown as number;
  });

  statusSelect?.addEventListener('change', () => {
    const v = statusSelect.value;
    filters.status = v === 'active' || v === 'inactive' ? v : '';
    persistFilters();
    applyFilters();
  });

  mappingSelect?.addEventListener('change', () => {
    const v = mappingSelect.value;
    filters.mapping = v === 'mapped' || v === 'unmapped' || v === 'in-vario' ? v : '';
    persistFilters();
    applyFilters();
  });

  // --- Legende-Popover (position:fixed, unter dem Info-Button positioniert) ---
  const legendBtn = host.querySelector<HTMLButtonElement>('#gambio-products-legend-btn');
  const legendPop = host.querySelector<HTMLDivElement>('#gambio-products-legend');
  if (legendBtn && legendPop) {
    const placeLegend = () => {
      const r = legendBtn.getBoundingClientRect();
      legendPop.style.top = `${r.bottom + 8}px`;
      let left = r.right - legendPop.offsetWidth;
      if (left < 12) left = 12;
      legendPop.style.left = `${left}px`;
    };
    legendBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      if (legendPop.hidden) {
        legendPop.hidden = false;
        placeLegend();
        legendBtn.classList.add('on');
        legendBtn.setAttribute('aria-expanded', 'true');
      } else {
        legendPop.hidden = true;
        legendBtn.classList.remove('on');
        legendBtn.setAttribute('aria-expanded', 'false');
      }
    });
    document.addEventListener('click', (e) => {
      if (legendPop.hidden) return;
      const t = e.target as Node;
      if (!legendPop.contains(t) && t !== legendBtn && !legendBtn.contains(t)) {
        legendPop.hidden = true;
        legendBtn.classList.remove('on');
        legendBtn.setAttribute('aria-expanded', 'false');
      }
    });
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && !legendPop.hidden) {
        legendPop.hidden = true;
        legendBtn.classList.remove('on');
        legendBtn.setAttribute('aria-expanded', 'false');
      }
    });
    window.addEventListener('resize', () => { if (!legendPop.hidden) placeLegend(); });
  }

  // Gespeicherten Filterzustand in die Controls spiegeln (load() baut die Abfrage damit auf).
  if (searchInput) searchInput.value = filters.search;
  if (statusSelect) statusSelect.value = filters.status;
  if (mappingSelect) mappingSelect.value = filters.mapping;

  await load();
}
